import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import trueKarmaLogo from "./tklogo.svg";

// ─── API Config ───────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api/v1";
const TERMS_URL = process.env.REACT_APP_TERMS_URL || "/terms";
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || "/privacy";
const STAFF_ROLES = ["ADMIN", "MEDICAL_ADMIN", "RECEPTIONIST"];
const staffRoleLabel = {
  ADMIN: "System Admin",
  MEDICAL_ADMIN: "Medical Center Admin",
  RECEPTIONIST: "Receptionist",
};
const normalizeRole = role => String(role || "").toUpperCase();
const isStaffRole = role => STAFF_ROLES.includes(normalizeRole(role));
const defaultRouteForRole = role => {
  const normalized = normalizeRole(role);
  if (normalized === "DOCTOR") return "/doctor/dashboard";
  if (isStaffRole(normalized)) return "/admin/dashboard";
  return "/patient/dashboard";
};

const api = {
  async request(method, path, body = null, token = null) {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });
    const data = await res.json();
    if (!data.success && res.status >= 400) throw { status: res.status, ...data };
    return data;
  },
  get: (path, token) => api.request("GET", path, null, token),
  post: (path, body, token) => api.request("POST", path, body, token),
  patch: (path, body, token) => api.request("PATCH", path, body, token),
  put: (path, body, token) => api.request("PUT", path, body, token),
};

// ─── Design tokens ────────────────────────────────────────────
const C = {
  primary: "#0F4C81",
  primaryLight: "#E8F1FA",
  primaryMid: "#185FA5",
  accent: "#0F6E56",
  accentLight: "#E1F5EE",
  danger: "#A32D2D",
  dangerLight: "#FCEBEB",
  warning: "#854F0B",
  warningLight: "#FAEEDA",
  success: "#3B6D11",
  successLight: "#EAF3DE",
  gray50: "#F8F9FA",
  gray100: "#F1EFE8",
  gray200: "#D3D1C7",
  gray400: "#888780",
  gray600: "#5F5E5A",
  gray800: "#444441",
  white: "#FFFFFF",
  border: "rgba(0,0,0,0.08)",
};

// ─── Specializations list (single source of truth) ────────────
const SPECIALIZATIONS = [
  "Cardiology","Neurology","Orthopedics","Pediatrics","Dermatology",
  "General Medicine","General Practice","Gynecology","Oncology","Psychiatry",
  "Radiology","Surgery","ENT","Ophthalmology","Urology","Nephrology",
  "Endocrinology","Gastroenterology","Pulmonology","Rheumatology",
];
const SORTED_SPECIALIZATIONS = [...SPECIALIZATIONS].sort((a, b) => a.localeCompare(b));

// ─── Shared UI Components ─────────────────────────────────────
function Badge({ children, color = "primary" }) {
  const colors = {
    primary: { bg: C.primaryLight, text: C.primary },
    success: { bg: C.successLight, text: C.success },
    danger: { bg: C.dangerLight, text: C.danger },
    warning: { bg: C.warningLight, text: C.warning },
    accent: { bg: C.accentLight, text: C.accent },
    gray: { bg: C.gray100, text: C.gray600 },
  };
  const { bg, text } = colors[color] || colors.primary;
  return (
    <span style={{
      background: bg, color: text, fontSize: 11, fontWeight: 600,
      padding: "2px 8px", borderRadius: 20, display: "inline-block",
      letterSpacing: "0.03em", textTransform: "uppercase",
    }}>{children}</span>
  );
}

function Card({ children, style = {}, onClick, className = "" }) {
  return (
    <div className={`app-card ${className}`.trim()} onClick={onClick} style={{
      background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
      padding: "20px 24px", cursor: onClick ? "pointer" : "default",
      transition: "box-shadow 0.15s", ...style,
    }}
      onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = "0 4px 16px rgba(15,76,129,0.1)")}
      onMouseLeave={e => onClick && (e.currentTarget.style.boxShadow = "none")}
    >
      {children}
    </div>
  );
}

function TrueKarmaBrand({ width = 180, padded = false, centered = false }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: centered ? "center" : "flex-start",
      alignItems: "center",
    }}>
      <div style={{
        background: padded ? C.white : "transparent",
        borderRadius: padded ? 8 : 0,
        padding: padded ? "8px 12px" : 0,
        boxShadow: padded ? "0 2px 8px rgba(0,0,0,0.12)" : "none",
      }}>
        <img src={trueKarmaLogo} alt="TrueKarma" style={{ display: "block", width, maxWidth: "100%", height: "auto" }} />
      </div>
    </div>
  );
}

function ReportDateInput({ label, value, onChange }) {
  const inputRef = useRef(null);
  const formatted = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "Select date";

  const openPicker = () => {
    if (inputRef.current?.showPicker) inputRef.current.showPicker();
    else inputRef.current?.click();
  };

  return (
    <div style={{ position: "relative" }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 6 }}>{label}</label>
      <button type="button" onClick={openPicker} style={{
        minWidth: 132, padding: "8px 12px", border: `1.5px solid ${C.border}`,
        borderRadius: 8, background: C.white, color: C.gray800, fontSize: 13,
        textAlign: "left", cursor: "pointer",
      }}>{formatted}</button>
      <input
        ref={inputRef}
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ position: "absolute", inset: 0, opacity: 0, pointerEvents: "none", width: 1, height: 1 }}
      />
    </div>
  );
}

function formatDisplayPhone(phoneNumber) {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (!digits) return "";

  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91-${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    return `+91-${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phoneNumber || "";
}

function formatTime12(time) {
  if (!time) return "";
  const [hour, minute] = time.substring(0, 5).split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time;
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

function dateValue(value) {
  if (!value) return "";
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return formatDateInput(value);
  }
  const text = String(value);
  const ymd = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const parsed = new Date(text);
  return Number.isFinite(parsed.getTime()) ? formatDateInput(parsed) : "";
}

function formatDisplayDate(value, options = { weekday: "short", day: "numeric", month: "short", year: "numeric" }, fallback = "Date not captured") {
  const normalized = dateValue(value);
  if (!normalized) return fallback;
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleDateString("en-IN", options) : fallback;
}

function formatAppointmentDateTime(appointment, options, fallback = "Date not captured") {
  const date = formatDisplayDate(appointment?.appointment_date, options, fallback);
  const time = formatTime12(appointment?.start_time);
  return time ? `${date} · ${time}` : date;
}

function appointmentDateValue(appointment) {
  return dateValue(appointment?.appointment_date);
}

function canModifyAppointment(appointment) {
  if (!appointment || ["CANCELLED", "COMPLETED", "NO_SHOW"].includes(appointment.status)) return false;
  const date = appointmentDateValue(appointment);
  const time = appointment.start_time?.substring(0, 5);
  if (!date || !time) return false;
  return new Date(`${date}T${time}:00`).getTime() - Date.now() >= 60 * 60 * 1000;
}

const APPOINTMENT_STATUS_BADGE = {
  CREATED: "primary",
  PENDING: "primary",
  CONFIRMED: "success",
  CHECKED_IN: "accent",
  IN_CONSULTATION: "warning",
  CANCELLED: "danger",
  COMPLETED: "gray",
  NO_SHOW: "warning",
};

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getScheduleRange(mode, anchorDate) {
  const anchor = new Date(`${anchorDate}T00:00:00`);
  if (!Number.isFinite(anchor.getTime())) return { from: anchorDate, to: anchorDate };
  if (mode === "week") {
    const start = addDays(anchor, -anchor.getDay());
    const end = addDays(start, 6);
    return { from: formatDateInput(start), to: formatDateInput(end) };
  }
  if (mode === "month") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: formatDateInput(start), to: formatDateInput(end) };
  }
  return { from: anchorDate, to: anchorDate };
}

function getAppointmentCountsByDate(appointments) {
  return appointments.reduce((counts, appointment) => {
    const date = appointmentDateValue(appointment);
    if (!date) return counts;
    counts[date] = (counts[date] || 0) + 1;
    return counts;
  }, {});
}

function Button({ children, onClick, variant = "primary", size = "md", disabled = false, style = {} }) {
  const variants = {
    primary: { bg: C.primary, color: C.white, border: C.primary },
    secondary: { bg: "transparent", color: C.primary, border: C.primary },
    danger: { bg: C.danger, color: C.white, border: C.danger },
    ghost: { bg: "transparent", color: C.gray600, border: "transparent" },
    success: { bg: C.accent, color: C.white, border: C.accent },
    warning: { bg: C.warning, color: C.white, border: C.warning },
  };
  const sizes = {
    sm: { padding: "6px 14px", fontSize: 13 },
    md: { padding: "9px 20px", fontSize: 14 },
    lg: { padding: "12px 28px", fontSize: 15 },
  };
  const v = variants[variant];
  const s = sizes[size];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? C.gray200 : v.bg,
      color: disabled ? C.gray400 : v.color,
      border: `1.5px solid ${disabled ? C.gray200 : v.border}`,
      borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
      fontWeight: 600, letterSpacing: "0.01em",
      transition: "all 0.15s", ...s, ...style,
    }}>
      {children}
    </button>
  );
}

function Input({ label, type = "text", value, onChange, placeholder, required, options, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 6 }}>
          {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
        </label>
      )}
      {type === "select" ? (
        <select value={value} onChange={e => onChange(e.target.value)} style={{
          width: "100%", padding: "9px 12px", border: `1.5px solid ${error ? C.danger : C.border}`,
          borderRadius: 8, fontSize: 14, background: C.white, color: C.gray800, outline: "none",
        }}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3}
          style={{
            width: "100%", padding: "9px 12px", border: `1.5px solid ${error ? C.danger : C.border}`,
            borderRadius: 8, fontSize: 14, color: C.gray800, outline: "none", boxSizing: "border-box",
            resize: "vertical", fontFamily: "inherit",
          }}
        />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{
            width: "100%", padding: "9px 12px", border: `1.5px solid ${error ? C.danger : C.border}`,
            borderRadius: 8, fontSize: 14, color: C.gray800, outline: "none", boxSizing: "border-box",
            transition: "border-color 0.15s",
          }}
          onFocus={e => e.target.style.borderColor = C.primary}
          onBlur={e => e.target.style.borderColor = error ? C.danger : C.border}
        />
      )}
      {error && <p style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

function MessagingConsents({ smsChecked, termsChecked, onSmsChange, onTermsChange }) {
  const linkStyle = { color: C.primary, fontWeight: 600, textDecoration: "none" };

  return (
    <div style={{ margin: "-8px 0 16px", border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.gray50 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <TrueKarmaBrand width={128} />
        <div style={{ fontSize: 11, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.08em" }}>Messaging Consent</div>
      </div>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: C.gray600, lineHeight: 1.45 }}>
        <input
          type="checkbox"
          checked={smsChecked}
          onChange={e => onSmsChange(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span>
          By checking this box, you consent to receive appointment text messages from TrueKarma.
          Reply STOP to opt out. Reply HELP for help. Msg & data rates may apply. Msg frequency may vary.
        </span>
      </label>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: C.gray600, lineHeight: 1.45, marginTop: 8 }}>
        <input
          type="checkbox"
          checked={termsChecked}
          onChange={e => onTermsChange(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span>
          I agree to <a href={TERMS_URL} target="_blank" rel="noreferrer" style={linkStyle}>Terms</a> and{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={linkStyle}>Privacy Policy</a>.
        </span>
      </label>
    </div>
  );
}

function Alert({ message, type = "error" }) {
  if (!message) return null;
  const colors = {
    error: { bg: C.dangerLight, text: C.danger, border: "#F09595" },
    success: { bg: C.successLight, text: C.success, border: "#97C459" },
    info: { bg: C.primaryLight, text: C.primary, border: "#85B7EB" },
  };
  const c = colors[type];
  return (
    <div style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
      {message}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 32, height: 32, border: `3px solid ${C.primaryLight}`,
        borderTopColor: C.primary, borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }} />
    </div>
  );
}

function ScheduleModeControl({ value, onChange }) {
  return (
    <div style={{ display: "flex", background: C.gray100, borderRadius: 8, padding: 3, border: `1px solid ${C.border}` }}>
      {[
        { value: "day", label: "Day" },
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
      ].map(option => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          style={{
            border: "none",
            borderRadius: 6,
            padding: "7px 12px",
            background: value === option.value ? C.white : "transparent",
            color: value === option.value ? C.primary : C.gray600,
            fontSize: 13,
            fontWeight: value === option.value ? 700 : 500,
            cursor: "pointer",
            boxShadow: value === option.value ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ScheduleCountCalendar({ mode, date, appointments, onSelectDate }) {
  const counts = getAppointmentCountsByDate(appointments);
  const range = getScheduleRange(mode, date);
  const start = new Date(`${range.from}T00:00:00`);
  const end = new Date(`${range.to}T00:00:00`);
  const cells = [];

  if (mode === "month") {
    for (let i = 0; i < start.getDay(); i += 1) cells.push(null);
  }

  for (let current = new Date(start); current <= end; current = addDays(current, 1)) {
    cells.push(formatDateInput(current));
  }

  const title = mode === "month"
    ? formatDisplayDate(date, { month: "long", year: "numeric" })
    : `${formatDisplayDate(range.from, { day: "numeric", month: "short" })} - ${formatDisplayDate(range.to, { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <Card style={{ padding: 0, overflow: "hidden", marginBottom: 28 }}>
      <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ fontSize: 16, color: C.gray800, margin: 0 }}>{mode === "month" ? "Monthly Schedule" : "Weekly Schedule"}</h3>
          <div style={{ fontSize: 12, color: C.gray400, marginTop: 3 }}>{title}</div>
        </div>
        <Badge color="danger">{appointments.length} appointments</Badge>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${C.border}` }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} style={{ padding: "9px 10px", fontSize: 11, fontWeight: 700, color: C.gray400, textAlign: "center", textTransform: "uppercase" }}>{day}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {cells.map((cellDate, index) => {
          const count = cellDate ? counts[cellDate] || 0 : 0;
          const selected = cellDate === date;
          return (
            <button
              key={cellDate || `blank-${index}`}
              disabled={!cellDate}
              onClick={() => cellDate && onSelectDate?.(cellDate)}
              style={{
                minHeight: mode === "month" ? 82 : 104,
                padding: 10,
                border: "none",
                borderRight: (index + 1) % 7 === 0 ? "none" : `1px solid ${C.border}`,
                borderBottom: index >= cells.length - 7 ? "none" : `1px solid ${C.border}`,
                background: !cellDate ? C.gray50 : selected ? C.primaryLight : C.white,
                cursor: cellDate ? "pointer" : "default",
                textAlign: "left",
              }}
            >
              {cellDate && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: selected ? C.primary : C.gray800 }}>
                    {formatDisplayDate(cellDate, mode === "week" ? { day: "numeric", month: "short" } : { day: "numeric" })}
                  </span>
                  {count > 0 && (
                    <span style={{
                      alignSelf: "flex-start",
                      background: C.dangerLight,
                      color: C.danger,
                      border: `1px solid #F09595`,
                      borderRadius: 20,
                      padding: "2px 8px",
                      fontSize: 11,
                      fontWeight: 800,
                    }}>
                      {count}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function AppointmentEditModal({ appointment, token, user, onClose, onSaved }) {
  const [date, setDate] = useState(() => appointmentDateValue(appointment));
  const [selectedTime, setSelectedTime] = useState(() => appointment?.start_time?.substring(0, 5) || "");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const modifiable = canModifyAppointment(appointment);
  const role = String(user?.role || "").toUpperCase();
  const canManageStatus = role === "DOCTOR" || isStaffRole(role);

  useEffect(() => {
    if (!appointment?.doctor_id || !date || !modifiable) return;
    (async () => {
      setLoadingSlots(true);
      setError("");
      try {
        const data = await api.get(`/availability/slots?doctor_id=${appointment.doctor_id}&date=${date}`, token);
        setSlots(data.available_slots || []);
      } catch (e) {
        setSlots([]);
        setError(e.message || "Could not load available slots.");
      }
      setLoadingSlots(false);
    })();
  }, [appointment, date, token, modifiable]);

  const handleCancel = async () => {
    if (!window.confirm("Cancel this appointment?")) return;
    setSaving(true); setError("");
    try {
      await api.patch(`/appointments/${appointment.appointment_id}/cancel`, { reason: "Cancelled from dashboard" }, token);
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to cancel appointment.");
    }
    setSaving(false);
  };

  const handleReschedule = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/appointments/${appointment.appointment_id}/reschedule`, {
        appointment_date: date,
        start_time: selectedTime,
      }, token);
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to reschedule appointment.");
    }
    setSaving(false);
  };

  const handleConfirm = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/appointments/${appointment.appointment_id}/confirm`, {}, token);
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to confirm appointment.");
    }
    setSaving(false);
  };

  const handleNoShow = async () => {
    if (!window.confirm("Mark this patient as no-show?")) return;
    setSaving(true); setError("");
    try {
      await api.put(`/appointments/${appointment.appointment_id}/no-show`, {
        marked_by: role,
        notes: "Patient did not arrive",
      }, token);
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to mark no-show.");
    }
    setSaving(false);
  };

  const currentDate = appointmentDateValue(appointment);
  const currentTime = appointment?.start_time?.substring(0, 5);
  const usableSlots = slots.filter(slot =>
    (!slot.is_booked && !slot.is_blocked) || (date === currentDate && slot.start_time === currentTime)
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.32)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 500, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, boxShadow: "0 18px 42px rgba(0,0,0,0.18)" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h3 style={{ fontSize: 18, margin: 0, color: C.gray800 }}>Manage Appointment</h3>
            <div style={{ fontSize: 13, color: C.gray400, marginTop: 4 }}>
              {appointment?.patient_name || appointment?.doctor_name || "Appointment"} · {appointment?.status}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div style={{ padding: 22 }}>
          <Alert message={error} />
          {!modifiable && (
            <Alert
              type="info"
              message="This appointment can no longer be cancelled or rescheduled. Changes are allowed only up to 1 hour before the appointment."
            />
          )}
          <div className="responsive-grid form-grid-2" style={{ gap: 12 }}>
            <Input label="Date" type="date" value={date} onChange={setDate} required />
            <Input
              label="Start Time"
              type="select"
              value={selectedTime}
              onChange={setSelectedTime}
              options={[
                { value: selectedTime, label: loadingSlots ? "Loading..." : selectedTime ? formatTime12(selectedTime) : "Select time" },
                ...usableSlots
                  .filter(slot => slot.start_time !== selectedTime)
                  .map(slot => ({ value: slot.start_time, label: `${formatTime12(slot.start_time)} - ${formatTime12(slot.end_time)}` })),
              ]}
              required
            />
          </div>
          <div style={{ fontSize: 12, color: C.gray400, margin: "-6px 0 18px" }}>
            Current: {formatDisplayDate(currentDate, { day: "numeric", month: "short", year: "numeric" })} · {formatTime12(currentTime)}
          </div>
          {canManageStatus && !["CANCELLED", "COMPLETED", "NO_SHOW"].includes(appointment.status) && (
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {appointment.status !== "CONFIRMED" && (
                <Button variant="success" size="lg" disabled={saving} onClick={handleConfirm} style={{ flex: 1 }}>Confirm</Button>
              )}
              <Button variant="warning" size="lg" disabled={saving} onClick={handleNoShow} style={{ flex: 1 }}>Mark No-show</Button>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="danger" size="lg" disabled={!modifiable || saving} onClick={handleCancel} style={{ flex: 1 }}>Cancel Appointment</Button>
            <Button size="lg" disabled={!modifiable || saving || !selectedTime} onClick={handleReschedule} style={{ flex: 1 }}>
              Reschedule
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR NAV ──────────────────────────────────────────────
function Sidebar({ user, onLogout }) {
  const navigate = useNavigate();
  const role = normalizeRole(user?.role);
  const doctorNavItems = [
    { path: "/doctor/dashboard", label: "Dashboard", icon: "⊞" },
    { path: "/doctor/schedule", label: "My Schedule", icon: "◷" },
    { path: "/doctor/queue", label: "Queue", icon: "☰" },
    { path: "/doctor/availability", label: "Set Availability", icon: "+" },
    { path: "/doctor/reports", label: "Reports", icon: "₹" },
    { path: "/profile", label: "My Profile", icon: "◉" },
  ];
  const patientNavItems = [
    { path: "/patient/dashboard", label: "My Appointments", icon: "⊞" },
    { path: "/doctors", label: "Find Doctors", icon: "⊕" },
    { path: "/profile", label: "My Profile", icon: "◉" },
  ];
  const adminNavItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: "⊞" },
    { path: "/doctors", label: "Doctors", icon: "♥" },
    { path: "/doctor/availability", label: "Set Availability", icon: "+" },
    { path: "/admin/walkin", label: "Walk-in", icon: "→" },
    { path: "/doctor/reports", label: "Reports", icon: "₹" },
    { path: "/admin/notifications", label: "Notifications", icon: "◎" },
    { path: "/profile", label: "My Profile", icon: "◉" },
  ];
  const receptionistNavItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: "⊞" },
    { path: "/doctors", label: "Doctors", icon: "♥" },
    { path: "/doctor/availability", label: "Set Availability", icon: "+" },
    { path: "/admin/walkin", label: "Walk-in", icon: "→" },
    { path: "/admin/notifications", label: "Notifications", icon: "◎" },
    { path: "/profile", label: "My Profile", icon: "◉" },
  ];
  const items = role === "DOCTOR" ? doctorNavItems : role === "RECEPTIONIST" ? receptionistNavItems : isStaffRole(role) ? adminNavItems : patientNavItems;
  const currentPath = window.location.pathname;

  return (
    <div className="app-sidebar" style={{ background: C.primary }}>
      <div className="sidebar-brand" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <TrueKarmaBrand width={154} padded />
      </div>
      <div className="sidebar-user" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="sidebar-avatar" style={{ background: "rgba(255,255,255,0.15)", color: C.white }}>
          {user?.name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="sidebar-user-text">
          <div style={{ fontSize: 13, fontWeight: 600, color: C.white }}>{user?.name}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{staffRoleLabel[role] || user?.role}</div>
        </div>
      </div>
      <nav className="sidebar-nav">
        {items.map(item => (
          <button className="sidebar-nav-item" key={item.path} onClick={() => navigate(item.path)} style={{
            background: currentPath === item.path ? "rgba(255,255,255,0.15)" : "transparent",
            color: currentPath === item.path ? C.white : "rgba(255,255,255,0.65)",
            fontWeight: currentPath === item.path ? 600 : 400,
          }}>
            <span style={{ fontSize: 16 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
        <button
          className="sidebar-nav-item sidebar-signout"
          aria-label="Sign out"
          onClick={() => {
            onLogout();
            navigate("/login", { replace: true });
          }}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "rgba(255,255,255,0.65)",
            fontWeight: 500,
          }}>
          <span style={{ fontSize: 16 }}>↩</span>
          Sign Out
        </button>
      </nav>
    </div>
  );
}

// ─── AUTH SCREEN ──────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      const data = await api.post("/auth/login", { email: form.email, password: form.password });
      onLogin(data.data.token, data.data.user);
    } catch (e) { setError(e.message || "Something went wrong."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(135deg, ${C.primary} 0%, #0A3560 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <TrueKarmaBrand width={224} padded centered />
        </div>
        <Card style={{ padding: 32 }}>
          <div style={{ display: "flex", background: C.gray100, borderRadius: 10, padding: 3, marginBottom: 24 }}>
            <button onClick={() => navigate("/login")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: C.white, color: C.primary, fontWeight: 600, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>Sign In</button>
            <button onClick={() => navigate("/register")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "transparent", color: C.gray400, fontWeight: 400, fontSize: 13, cursor: "pointer" }}>Register</button>
          </div>
          <Alert message={error} type="error" />
          <Input label="Email" type="email" value={form.email} onChange={v => set("email", v)} placeholder="email@hospital.com" required />
          <Input label="Password" type="password" value={form.password} onChange={v => set("password", v)} placeholder="Min 8 characters" required />
          <Button onClick={handleSubmit} disabled={loading} style={{ width: "100%", marginTop: 8 }} size="lg">
            {loading ? "Please wait..." : "Sign In"}
          </Button>
        </Card>
      </div>
    </div>
  );
}

function RegisterScreen({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "PATIENT", phone_number: "", sms_opt_in: false, terms_accepted: false, specialization: "", consultation_fee: "", clinic_name: "", clinic_address: "", age: "", gender: "MALE", city: "", state: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      const body = { name: form.name, email: form.email, password: form.password, role: form.role, city: form.city || null, state: form.state || null };
      if (form.role === "DOCTOR") {
        body.specialization = form.specialization;
        body.consultation_fee = parseFloat(form.consultation_fee) || null;
        body.clinic_name = form.clinic_name || null;
        body.clinic_address = form.clinic_address || null;
      }
      if (isStaffRole(form.role)) {
        body.clinic_name = form.clinic_name || null;
        body.clinic_address = form.clinic_address || null;
      }
      if (form.role === "PATIENT") { body.age = parseInt(form.age) || null; body.gender = form.gender; body.phone_number = form.phone_number; body.sms_opt_in = form.sms_opt_in; body.terms_accepted = form.terms_accepted; }
      const data = await api.post("/auth/register", body);
      onLogin(data.data.token, data.data.user);
    } catch (e) { setError(e.message || "Something went wrong."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(135deg, ${C.primary} 0%, #0A3560 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <TrueKarmaBrand width={224} padded centered />
        </div>
        <Card style={{ padding: 32 }}>
          <div style={{ display: "flex", background: C.gray100, borderRadius: 10, padding: 3, marginBottom: 24 }}>
            <button onClick={() => navigate("/login")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "transparent", color: C.gray400, fontWeight: 400, fontSize: 13, cursor: "pointer" }}>Sign In</button>
            <button onClick={() => navigate("/register")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: C.white, color: C.primary, fontWeight: 600, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>Register</button>
          </div>
          <Alert message={error} type="error" />
          <Input label="Full Name" value={form.name} onChange={v => set("name", v)} placeholder="Dr. Priya Sharma" required />
          <Input label="Email" type="email" value={form.email} onChange={v => set("email", v)} placeholder="email@hospital.com" required />
          <Input label="Password" type="password" value={form.password} onChange={v => set("password", v)} placeholder="Min 8 characters" required />
          <Input label="Role" type="select" value={form.role} onChange={v => set("role", v)} options={[
            { value: "PATIENT", label: "Patient" },
            { value: "DOCTOR", label: "Doctor" },
            { value: "MEDICAL_ADMIN", label: "Medical Center Admin" },
            { value: "RECEPTIONIST", label: "Receptionist" },
            { value: "ADMIN", label: "System Admin" },
          ]} />
          {form.role === "DOCTOR" && (
            <>
              <Input label="Specialization" type="select" value={form.specialization} onChange={v => set("specialization", v)}
                options={[{ value: "", label: "Select specialization..." }, ...SORTED_SPECIALIZATIONS.map(s => ({ value: s, label: s }))]} />
              <Input label="Consultation Fee (₹)" type="number" value={form.consultation_fee} onChange={v => set("consultation_fee", v)} placeholder="1500" />
              <Input label="Primary Practice / Clinic Name" value={form.clinic_name} onChange={v => set("clinic_name", v)} placeholder="TrueKarma Medical Center" />
              <Input label="Primary Practice Address" value={form.clinic_address} onChange={v => set("clinic_address", v)} placeholder="Street, city, state" />
            </>
          )}
          {isStaffRole(form.role) && (
            <>
              <Input label="Medical Center / Clinic Name" value={form.clinic_name} onChange={v => set("clinic_name", v)} placeholder="TrueKarma Medical Center" />
              <Input label="Medical Center / Clinic Address" value={form.clinic_address} onChange={v => set("clinic_address", v)} placeholder="Street, city, state" />
            </>
          )}
          {form.role === "PATIENT" && (
            <>
              <Input label="Phone Number" value={form.phone_number} onChange={v => set("phone_number", v)} placeholder="+1 555 123 4567" required />
              <MessagingConsents
                smsChecked={form.sms_opt_in}
                termsChecked={form.terms_accepted}
                onSmsChange={v => set("sms_opt_in", v)}
                onTermsChange={v => set("terms_accepted", v)}
              />
              <Input label="Age" type="number" value={form.age} onChange={v => set("age", v)} placeholder="32" />
              <Input label="Gender" type="select" value={form.gender} onChange={v => set("gender", v)} options={[
                { value: "MALE", label: "Male" },
                { value: "FEMALE", label: "Female" },
                { value: "OTHER", label: "Other" },
              ]} />
            </>   
          )}
          <Input label="City" value={form.city} onChange={v => set("city", v)} placeholder="San Francisco" />
          <Input label="State" value={form.state} onChange={v => set("state", v)} placeholder="California" />
          <Button onClick={handleSubmit} disabled={loading} style={{ width: "100%", marginTop: 8 }} size="lg">
            {loading ? "Please wait..." : "Create Account"}
          </Button>
        </Card>
      </div>
    </div>
  );
}

// ─── PATIENT DASHBOARD ────────────────────────────────────────
function PatientDashboard({ token, user }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const pidData = await api.get(`/appointments/my-patient-id/${user.user_id}`, token);
      const pid = pidData.patient_id;
      const data = await api.get(`/appointments/patient/${pid}`, token);
      setAppointments(data.data || []);
      setError("");
    } catch (e) { setError("Could not load appointments."); }
    setLoading(false);
  }, [token, user]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const stats = [
    { label: "Total Booked", value: appointments.length, color: C.primary },
    { label: "Upcoming", value: appointments.filter(a => {
      const date = appointmentDateValue(a);
      return date && new Date(`${date}T23:59:59`) >= new Date();
    }).length, color: C.accent },
    { label: "Completed", value: appointments.filter(a => a.status === "COMPLETED").length, color: C.success },
    { label: "Cancelled", value: appointments.filter(a => a.status === "CANCELLED").length, color: C.danger },
  ];
  const statusColor = { CREATED: "primary", CONFIRMED: "success", CHECKED_IN: "accent", CANCELLED: "danger", COMPLETED: "gray", NO_SHOW: "warning" };

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>My Appointments</h1>
        <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Welcome back, {user.name}</p>
      </div>
      <div className="responsive-grid stats-grid-4" style={{ marginBottom: 28 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: C.gray800, margin: 0 }}>Appointment History</h2>
        <Button onClick={() => navigate("/doctors")} size="sm">+ Book New</Button>
      </div>
      {loading ? <Spinner /> : error ? <Alert message={error} /> : appointments.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
          <p style={{ color: C.gray400, fontSize: 14 }}>No appointments yet.</p>
          <Button onClick={() => navigate("/doctors")} style={{ marginTop: 12 }}>Find a Doctor</Button>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {appointments.map(a => (
            <Card key={a.appointment_id} className="mobile-stack-row" onClick={() => setSelectedAppointment(a)}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.gray800 }}>{a.doctor_name || "Doctor"}</div>
                <div style={{ fontSize: 12, color: C.gray400, marginTop: 2 }}>{a.specialization} · {a.appointment_type}</div>
                <div style={{ fontSize: 13, color: C.gray600, marginTop: 4 }}>
                  {formatAppointmentDateTime(a, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <Badge color={statusColor[a.status] || "gray"}>{a.status || "Pending"}</Badge>
                {canModifyAppointment(a) && <span style={{ fontSize: 11, color: C.primary, fontWeight: 600 }}>Manage</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
      {selectedAppointment && (
        <AppointmentEditModal
          appointment={selectedAppointment}
          token={token}
          user={user}
          onClose={() => setSelectedAppointment(null)}
          onSaved={fetchAppointments}
        />
      )}
    </div>
  );
}

// ─── DOCTOR SEARCH ────────────────────────────────────────────
function SearchDoctors({ token, user }) {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [spec, setSpec] = useState("");
  const [sortBy, setSortBy] = useState("");

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("name", search);
      if (spec) params.append("specialization", spec);
      if (isStaffRole(user?.role)) {
        const profile = await api.get("/auth/me", token);
        const scope = profile.data?.staff || null;
        if (normalizeRole(user?.role) !== "ADMIN" && !scope?.clinic_name && !scope?.clinic_address) {
          setDoctors([]);
          setLoading(false);
          return;
        }
        if (scope?.clinic_name) params.append("clinic_name", scope.clinic_name);
        if (scope?.clinic_address) params.append("clinic_address", scope.clinic_address);
      }
      const data = await api.get(`/doctors?${params}`, token);
      setDoctors(data.data || []);
    } catch (e) { setDoctors([]); }
    setLoading(false);
  }, [search, spec, token, user]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const sortedDoctors = [...doctors].sort((a, b) => {
    if (!sortBy) return 0;
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    const feeA = Number(a.consultation_fee) || 0;
    const feeB = Number(b.consultation_fee) || 0;
    const expA = Number(a.experience_years) || 0;
    const expB = Number(b.experience_years) || 0;
    switch (sortBy) {
      case "name-desc": return nameB.localeCompare(nameA);
      case "specialization-asc": return (a.specialization || "").localeCompare(b.specialization || "") || nameA.localeCompare(nameB);
      case "fee-asc": return feeA - feeB || nameA.localeCompare(nameB);
      case "fee-desc": return feeB - feeA || nameA.localeCompare(nameB);
      case "experience-desc": return expB - expA || nameA.localeCompare(nameB);
      case "experience-asc": return expA - expB || nameA.localeCompare(nameB);
      case "name-asc": default: return nameA.localeCompare(nameB);
    }
  });

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>Find Doctors</h1>
        <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Search and book appointments with our specialists</p>
      </div>
      <Card style={{ marginBottom: 20, padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <input placeholder="Search by doctor name..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: "none" }} />
          <select value={spec} onChange={e => setSpec(e.target.value)}
            style={{ padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, background: C.white, minWidth: 200 }}>
            <option value="">All Specializations</option>
            {SORTED_SPECIALIZATIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, background: C.white, minWidth: 190 }}>
            <option value="">Sort Options</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="specialization-asc">Specialization A-Z</option>
            <option value="fee-asc">Fee Low to High</option>
            <option value="fee-desc">Fee High to Low</option>
            <option value="experience-desc">Experience High to Low</option>
            <option value="experience-asc">Experience Low to High</option>
          </select>
        </div>
      </Card>
      {loading ? <Spinner /> : doctors.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <p style={{ color: C.gray400 }}>No doctors found. Try a different search.</p>
        </Card>
      ) : (
        <div className="responsive-grid form-grid-2">
          {sortedDoctors.map(d => (
            <Card key={d.doctor_id} onClick={() => navigate(`/book/doctor/${d.doctor_id}`, { state: { doctor: d } })} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: C.primary, flexShrink: 0 }}>
                  {d.name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.gray800 }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: C.gray400, marginTop: 2 }}>{d.specialization || "General"}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {d.experience_years && <Badge color="gray">{d.experience_years} yrs exp</Badge>}
                    {d.consultation_fee && <Badge color="accent">₹{d.consultation_fee}</Badge>}
                  </div>
                </div>
              </div>
              <Button size="sm" style={{ width: "100%", marginTop: 14 }}>Book Appointment</Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BOOK APPOINTMENT ─────────────────────────────────────────
function BookAppointment({ token, user }) {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [apptType, setApptType] = useState("IN_PERSON");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [reason, setReason] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [patientId, setPatientId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get(`/doctors/${doctorId}`, token);
        setDoctor(data.data);
        const pidData = await api.get(`/appointments/my-patient-id/${user.user_id}`, token);
        setPatientId(pidData.patient_id);
        setPhoneNumber(pidData.phone_number || "");
      } catch (e) { setPatientId(user.user_id); }
    })();
  }, [token, user, doctorId]);

  const fetchSlots = async (d) => {
    if (!d) return;
    setLoadingSlots(true); setSlotsError(""); setSlots([]); setSelectedSlot(null);
    try {
      const data = await api.get(`/availability/slots?doctor_id=${doctorId}&date=${d}`, token);
      setSlots(data.available_slots || []);
      if (!data.available_slots?.length) setSlotsError("No slots available for this date.");
    } catch (e) { setSlotsError(e.message || "No availability set for this date."); }
    setLoadingSlots(false);
  };

  const handleBook = async () => {
    if (!selectedSlot) return setError("Please select a time slot.");
    if (!phoneNumber.trim()) return setError("Please enter your phone number.");
    if (!smsOptIn) return setError("Please sign up for SMS appointment messages to continue.");
    if (!termsAccepted) return setError("Please agree to the Terms and Privacy Policy to continue.");
    setError(""); setBooking(true);
    try {
      await api.post("/appointments", {
        patient_id: patientId || user.user_id,
        doctor_id: parseInt(doctorId),
        appointment_date: date,
        start_time: selectedSlot.start_time,
        appointment_type: apptType,
        phone_number: phoneNumber,
        sms_opt_in: smsOptIn,
        terms_accepted: termsAccepted,
        reason_for_visit: reason,
      }, token);
      setSuccess(true);
    } catch (e) { setError(e.message || "Failed to book appointment."); }
    setBooking(false);
  };

  if (!doctor) return <Spinner />;

  if (success) return (
    <div className="page" style={{ maxWidth: 600 }}>
      <Card style={{ textAlign: "center", padding: 48 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.successLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.success, margin: "0 0 8px" }}>Appointment Booked!</h2>
        <p style={{ color: C.gray400, fontSize: 14, margin: "0 0 24px" }}>
          Your appointment with {doctor.name} on {date} at {selectedSlot?.start_time} has been confirmed.
        </p>
        <Button onClick={() => navigate("/patient/dashboard")}>View My Appointments</Button>
      </Card>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 680 }}>
      <button onClick={() => navigate("/doctors")} style={{ background: "none", border: "none", cursor: "pointer", color: C.gray400, fontSize: 13, marginBottom: 20, padding: 0 }}>
        ← Back to Doctors
      </button>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: "0 0 20px", fontFamily: "'Playfair Display', serif" }}>Book Appointment</h1>
      <Card style={{ marginBottom: 24, display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: C.primary, flexShrink: 0 }}>
          {doctor.name?.charAt(0)}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.gray800 }}>{doctor.name}</div>
          <div style={{ fontSize: 13, color: C.gray400 }}>{doctor.specialization} · ₹{doctor.consultation_fee}</div>
        </div>
      </Card>
      <Alert message={error} type="error" />
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 6 }}>Select Date *</label>
        <input type="date" value={date} min={new Date().toISOString().split("T")[0]}
          onChange={e => { setDate(e.target.value); fetchSlots(e.target.value); }}
          style={{ padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" }} />
      </div>
      {date && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 10 }}>Available Slots</label>
          {loadingSlots ? <Spinner /> : slotsError ? <Alert message={slotsError} type="info" /> : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {slots.map((slot, i) => (
                <button key={i} disabled={slot.is_booked} onClick={() => setSelectedSlot(slot)} style={{
                  padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500,
                  cursor: slot.is_booked ? "not-allowed" : "pointer",
                  background: slot.is_booked ? C.gray100 : selectedSlot?.start_time === slot.start_time ? C.primary : C.white,
                  color: slot.is_booked ? C.gray400 : selectedSlot?.start_time === slot.start_time ? C.white : C.gray800,
                  border: `1.5px solid ${slot.is_booked ? C.gray200 : selectedSlot?.start_time === slot.start_time ? C.primary : C.border}`,
                  textDecoration: slot.is_booked ? "line-through" : "none", transition: "all 0.15s",
                }}>{slot.start_time}</button>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 8 }}>Appointment Type</label>
        <div style={{ display: "flex", gap: 10 }}>
          {["IN_PERSON", "VIDEO"].map(t => (
            <button key={t} onClick={() => setApptType(t)} style={{
              padding: "8px 20px", borderRadius: 8, fontSize: 13, cursor: "pointer",
              background: apptType === t ? C.primary : C.white, color: apptType === t ? C.white : C.gray600,
              border: `1.5px solid ${apptType === t ? C.primary : C.border}`,
              fontWeight: apptType === t ? 600 : 400, transition: "all 0.15s",
            }}>{t === "IN_PERSON" ? "In Person" : "Video Call"}</button>
          ))}
        </div>
      </div>
      <Input label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} placeholder="+1 555 123 4567" required />
      <MessagingConsents
        smsChecked={smsOptIn}
        termsChecked={termsAccepted}
        onSmsChange={setSmsOptIn}
        onTermsChange={setTermsAccepted}
      />
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 6 }}>Reason for Visit</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Brief description of your concern..." rows={3}
          style={{ width: "100%", padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, resize: "vertical", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
      </div>
      <Button onClick={handleBook} disabled={booking || !date || !selectedSlot || !phoneNumber.trim() || !smsOptIn || !termsAccepted} size="lg" style={{ width: "100%" }}>
        {booking ? "Booking..." : "Confirm Appointment"}
      </Button>
    </div>
  );
}

// ─── DOCTOR DASHBOARD ─────────────────────────────────────────
function DoctorDashboard({ token, user }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [doctorId, setDoctorId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [date, setDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });
  const [scheduleMode, setScheduleMode] = useState("day");
  const [bookingSlot, setBookingSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    patient_id: "",
    patient_name: "",
    phone_number: "",
    sms_opt_in: false,
    terms_accepted: false,
    appointment_type: "IN_PERSON",
    reason_for_visit: "",
  });
  const [bookingSaving, setBookingSaving] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const drData = await api.get(`/doctors?page=1&limit=50`, token);
        const me = drData.data?.find(d => d.email === user.email);
        setDoctorId(me?.doctor_id || user.user_id);
      } catch (e) { setDoctorId(user.user_id); }
    })();
  }, [token, user]);

  const fetchAppointments = useCallback(async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const range = getScheduleRange(scheduleMode, date);
      const query = scheduleMode === "day"
        ? `date=${date}`
        : `from=${range.from}&to=${range.to}`;
      const data = await api.get(`/appointments/doctor/${doctorId}?${query}`, token);
      setAppointments(data.data || []);
    } catch (e) { setAppointments([]); }
    setLoading(false);
  }, [token, doctorId, date, scheduleMode]);

  const fetchSlots = useCallback(async () => {
    if (!doctorId) return;
    setSlotsLoading(true); setSlotsError("");
    try {
      const data = await api.get(`/availability/slots?doctor_id=${doctorId}&date=${date}`, token);
      setSlots(data.available_slots || []);
    } catch (e) { setSlots([]); setSlotsError(e.message || "No availability set for this date."); }
    setSlotsLoading(false);
  }, [token, doctorId, date]);

  const fetchBlocks = useCallback(async () => {
    if (!doctorId) return;
    setBlocksLoading(true);
    try {
      const data = await api.get(`/availability/blocks/${doctorId}?date=${date}`, token);
      setBlocks(data.data || []);
    } catch (e) { setBlocks([]); }
    setBlocksLoading(false);
  }, [token, doctorId, date]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);
  useEffect(() => { fetchSlots(); }, [fetchSlots]);
  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const openBooking = (slot) => {
    setBookingSlot(slot);
    setBookingForm({ patient_id: "", patient_name: "", phone_number: "", sms_opt_in: false, terms_accepted: false, appointment_type: "IN_PERSON", reason_for_visit: "" });
    setBookingError("");
    setBookingSuccess("");
  };

  const closeBooking = () => {
    if (bookingSaving) return;
    setBookingSlot(null);
    setBookingError("");
  };

  const setBookingField = (key, value) => {
    setBookingForm(prev => ({ ...prev, [key]: value }));
  };

  const handleDoctorBook = async () => {
    if (!doctorId || !bookingSlot) return;
    if (!bookingForm.patient_id && !bookingForm.patient_name.trim()) {
      setBookingError("Patient name is required unless you enter an existing patient ID.");
      return;
    }
    if (!bookingForm.phone_number.trim()) {
      setBookingError("Phone number is required for appointment messages and doctor calls.");
      return;
    }
    if (!bookingForm.sms_opt_in) {
      setBookingError("SMS opt-in is required before sending appointment messages.");
      return;
    }
    if (!bookingForm.terms_accepted) {
      setBookingError("Terms and Privacy Policy agreement is required before creating the appointment.");
      return;
    }

    setBookingSaving(true);
    setBookingError("");
    setBookingSuccess("");

    try {
      await api.post("/appointments/doctor-book", {
        doctor_id: doctorId,
        appointment_date: date,
        start_time: bookingSlot.start_time,
        patient_id: bookingForm.patient_id ? parseInt(bookingForm.patient_id) : undefined,
        patient_name: bookingForm.patient_name.trim() || undefined,
        phone_number: bookingForm.phone_number.trim() || undefined,
        sms_opt_in: bookingForm.sms_opt_in,
        terms_accepted: bookingForm.terms_accepted,
        appointment_type: bookingForm.appointment_type,
        reason_for_visit: bookingForm.reason_for_visit.trim() || undefined,
      }, token);

      setBookingSuccess(`Appointment created for ${bookingSlot.start_time}.`);
      setBookingSlot(null);
      await Promise.all([fetchAppointments(), fetchSlots()]);
    } catch (e) {
      setBookingError(e.message || "Failed to create appointment.");
    }

    setBookingSaving(false);
  };

  const statusColor = { CREATED: "primary", CONFIRMED: "success", CHECKED_IN: "accent", IN_CONSULTATION: "warning", COMPLETED: "gray", CANCELLED: "danger", NO_SHOW: "warning" };
  const appointmentByStartTime = appointments.reduce((map, appt) => {
    if (appt.start_time) map[appt.start_time.substring(0, 5)] = appt;
    return map;
  }, {});
  const calendarCounts = {
    total: slots.length,
    available: slots.filter(s => !s.is_booked && !s.is_blocked).length,
    blocked: slots.filter(s => s.is_blocked).length,
  };
  const scheduleTitle = scheduleMode === "day" ? "Availability Calendar" : scheduleMode === "week" ? "Weekly Schedule" : "Monthly Schedule";
  const openScheduleDay = (selectedDate) => {
    setDate(selectedDate);
    setScheduleMode("day");
  };

  return (
    <div className="page" style={{ maxWidth: 1040 }}>
      <div className="toolbar-row" style={{ alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>My Schedule</h1>
          <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Welcome, {user.name}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <ScheduleModeControl value={scheduleMode} onChange={setScheduleMode} />
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ padding: "8px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14 }} />
        </div>
      </div>
      <div className="responsive-grid stats-grid-4" style={{ marginBottom: 28 }}>
        {[
          { label: "Appointments", value: appointments.length, color: C.primary },
          { label: "Confirmed", value: appointments.filter(a => a.status === "CONFIRMED").length, color: C.accent },
          { label: "Open Slots", value: calendarCounts.available, color: C.success },
          { label: "Blocked Times", value: blocks.length, color: C.warning },
        ].map(s => (
          <div key={s.label} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
          </div>
        ))}
      </div>
      {bookingSuccess && <Alert message={bookingSuccess} type="success" />}
      <div className="toolbar-row" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: C.gray800, margin: 0 }}>{scheduleTitle}</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {scheduleMode === "day" && calendarCounts.total > 0 && (<><Badge color="success">Available</Badge><Badge color="primary">Booked</Badge><Badge color="warning">Blocked</Badge></>)}
          {scheduleMode === "day" && <Button size="sm" variant="secondary" onClick={() => navigate("/doctor/availability")}>Manage Blocks</Button>}
        </div>
      </div>
      {scheduleMode !== "day" ? (
        loading ? <Spinner /> : (
          <ScheduleCountCalendar
            mode={scheduleMode}
            date={date}
            appointments={appointments}
            onSelectDate={openScheduleDay}
          />
        )
      ) : slotsLoading ? <Spinner /> : slotsError ? (
        <Card style={{ textAlign: "center", padding: 34, marginBottom: 28 }}><p style={{ color: C.gray400, fontSize: 14, margin: 0 }}>{slotsError}</p></Card>
      ) : slots.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 34, marginBottom: 28 }}><p style={{ color: C.gray400, fontSize: 14, margin: 0 }}>No slots generated for this date.</p></Card>
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 28 }}>
          {slots.map((slot, i) => {
            const appointment = appointmentByStartTime[slot.start_time];
            const isBooked = Boolean(appointment);
            const isBlocked = slot.is_blocked;
            const status = isBlocked ? "Blocked" : isBooked ? "Booked" : "Available";
            const color = isBlocked ? C.warning : isBooked ? C.primary : C.accent;
            const bg = isBlocked ? C.warningLight : isBooked ? C.primaryLight : C.accentLight;
            const canBook = !isBooked && !isBlocked;
            return (
              <div
                key={`${slot.start_time}-${slot.end_time}`}
                onClick={() => canBook && openBooking(slot)}
                onKeyDown={e => {
                  if (canBook && (e.key === "Enter" || e.key === " ")) openBooking(slot);
                }}
                role={canBook ? "button" : undefined}
                tabIndex={canBook ? 0 : undefined}
                style={{
                  padding: "10px 14px", borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                  background: i % 2 === 0 ? C.white : C.gray50,
                  cursor: canBook ? "pointer" : "default",
                  outline: "none",
                }}
              >
                <div className="doctor-slot-row">
                <div style={{ fontSize: 13, color: C.gray600, fontWeight: 700, paddingTop: 10 }}>{slot.start_time} - {slot.end_time}</div>
                <div style={{ minHeight: 42, borderLeft: `4px solid ${color}`, background: bg, borderRadius: 8, padding: "9px 12px" }}>
                  <div style={{ fontSize: 13, color, fontWeight: 700 }}>{status}</div>
                  <div style={{ fontSize: 12, color: isBooked ? C.primaryMid : color, marginTop: 2 }}>
                    {isBlocked ? slot.block_reason || "Blocked time" : isBooked ? `${appointment.patient_name || "Patient"} · ${appointment.reason_for_visit || appointment.appointment_type || "Appointment"}` : "Click to create an appointment"}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                  {canBook && <Button size="sm" variant="secondary" onClick={e => { e.stopPropagation(); openBooking(slot); }}>Book</Button>}
                  <Badge color={isBlocked ? "warning" : isBooked ? (statusColor[appointment?.status] || "primary") : "success"}>
                    {isBooked ? (appointment.status || "Booked") : status}
                  </Badge>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {bookingSlot && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.32)", zIndex: 20,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{
            width: "100%", maxWidth: 460, background: C.white, borderRadius: 10,
            border: `1px solid ${C.border}`, boxShadow: "0 18px 42px rgba(0,0,0,0.18)",
          }}>
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div>
                <h3 style={{ fontSize: 18, margin: 0, color: C.gray800 }}>Create Appointment</h3>
                <div style={{ fontSize: 13, color: C.gray400, marginTop: 4 }}>
                  {formatDisplayDate(date, { weekday: "short", day: "numeric", month: "short" })} · {bookingSlot.start_time} - {bookingSlot.end_time}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={closeBooking}>Close</Button>
            </div>
            <div style={{ padding: 22 }}>
              <Alert message={bookingError} />
              <Input
                label="Patient Name"
                value={bookingForm.patient_name}
                onChange={v => setBookingField("patient_name", v)}
                placeholder="Full name"
                required={!bookingForm.patient_id}
              />
              <Input
                label="Existing Patient ID"
                type="number"
                value={bookingForm.patient_id}
                onChange={v => setBookingField("patient_id", v)}
                placeholder="Optional"
              />
              <Input
                label="Phone Number"
                value={bookingForm.phone_number}
                onChange={v => setBookingField("phone_number", v)}
                placeholder="+1 555 123 4567"
                required
              />
              <MessagingConsents
                smsChecked={bookingForm.sms_opt_in}
                termsChecked={bookingForm.terms_accepted}
                onSmsChange={v => setBookingField("sms_opt_in", v)}
                onTermsChange={v => setBookingField("terms_accepted", v)}
              />
              <Input
                label="Appointment Type"
                type="select"
                value={bookingForm.appointment_type}
                onChange={v => setBookingField("appointment_type", v)}
                options={[
                  { value: "IN_PERSON", label: "In person" },
                  { value: "VIDEO", label: "Video" },
                ]}
                required
              />
              <Input
                label="Reason for Visit"
                type="textarea"
                value={bookingForm.reason_for_visit}
                onChange={v => setBookingField("reason_for_visit", v)}
                placeholder="Symptoms, follow-up, notes"
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <Button variant="secondary" onClick={closeBooking} disabled={bookingSaving}>Cancel</Button>
                <Button onClick={handleDoctorBook} disabled={bookingSaving || !bookingForm.phone_number.trim() || !bookingForm.sms_opt_in || !bookingForm.terms_accepted || (!bookingForm.patient_id && !bookingForm.patient_name.trim())}>
                  {bookingSaving ? "Creating..." : "Create Appointment"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DOCTOR QUEUE ─────────────────────────────────────────────
function DoctorQueue({ token, user }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorId, setDoctorId] = useState(null);
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    (async () => {
      try {
        const drData = await api.get("/doctors?page=1&limit=50", token);
        const me = drData.data?.find(d => d.email === user.email);
        if (me) setDoctorId(me.doctor_id);
      } catch (e) {}
    })();
  }, [token, user]);

  useEffect(() => {
    if (!doctorId) return;
    (async () => {
      try {
        const data = await api.get(`/queue/doctor/${doctorId}?date=${today}`, token);
        setQueue(data.queue || []);
      } catch (e) { setQueue([]); }
      setLoading(false);
    })();
  }, [token, doctorId, today]);

  return (
    <div className="page" style={{ maxWidth: 700 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: "0 0 24px", fontFamily: "'Playfair Display', serif" }}>Today's Queue</h1>
      {loading ? <Spinner /> : queue.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <p style={{ color: C.gray400 }}>No patients in queue today.</p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {queue.map(q => (
            <Card key={q.appointment_id} style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: C.primary }}>{q.queue_position}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.gray800 }}>{q.patient_name || "Walk-in Patient"}</div>
                <div style={{ fontSize: 12, color: C.gray400, marginTop: 2 }}>Est. wait: {q.estimated_wait_minutes} mins</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{q.start_time}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
// ─── DOCTOR REPORTS ───────────────────────────────────────────
function DoctorReports({ token, user }) {
  const role = normalizeRole(user?.role);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(role === "DOCTOR");
  const [error, setError] = useState("");
  const [doctorId, setDoctorId] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [cityFilter, setCityFilter] = useState("");
  const [centerFilter, setCenterFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (role !== "DOCTOR") {
      setLoading(false);
      setError("Appointment reports are currently available only for doctor accounts.");
      return;
    }
    (async () => {
      try {
        const drData = await api.get("/doctors?page=1&limit=50", token);
        const me = drData.data?.find(d => d.email === user.email);
        if (me) setDoctorId(me.doctor_id);
        else {
          setError("No doctor profile is linked to this account.");
          setLoading(false);
        }
      } catch (e) {
        setError("Could not load doctor profile.");
        setLoading(false);
      }
    })();
  }, [role, token, user]);

  const fetchReports = async (doctorIdParam, fromOverride, toOverride) => {
    const id = doctorIdParam || doctorId;
    if (!id) return;
    const fromVal = fromOverride !== undefined ? fromOverride : from;
    const toVal   = toOverride  !== undefined ? toOverride  : to;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (fromVal) params.append("from", fromVal);
      if (toVal)   params.append("to",   toVal);
      const data = await api.get(`/doctors/${id}/reports?${params}`, token);
      setReports(data.data || []);
      setSummary(data.summary || null);
      setPage(1); // reset to page 1 on every fresh fetch
    } catch (e) { setError("Could not load reports."); }
    setLoading(false);
  };

  useEffect(() => {
    if (doctorId) fetchReports(doctorId);
  }, [doctorId]);

  const valueOrPlaceholder = (value, placeholder = "Not captured") => {
    if (value === null || value === undefined || value === "") return placeholder;
    return value;
  };
  const formatCurrency = (value) => {
    const amount = parseFloat(value);
    return Number.isFinite(amount) ? `₹${amount.toLocaleString()}` : "Not captured";
  };
  const formatTime12 = (time) => {
    if (!time) return "";
    const [hour, minute] = time.substring(0, 5).split(":").map(Number);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time;
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
  };
  const formatReportDateTime = (r) => {
    const date = formatDisplayDate(r.appointment_date, { day: "numeric", month: "short", year: "numeric" }, "Not captured");
    const time = formatTime12(r.start_time);
    return time ? `${date} · ${time}` : date;
  };
  const getDoctorName = (r) => valueOrPlaceholder(r.doctor_name, "Doctor not captured");
  const getCenterName = (r) => r.medical_center || r.clinic_name || r.clinic_address || "Center not captured";
  const getPatientName = (r) => valueOrPlaceholder(r.patient_name, "Patient not captured");
  const getPatientMobile = (r) => {
    const formatted = formatDisplayPhone(r.contact_number);
    return valueOrPlaceholder(formatted, "Mobile not captured");
  };
  const formatDoctorCenter = (r) => {
    return `${getDoctorName(r)} · ${getCenterName(r)}`;
  };
  const formatPatientMobile = (r) => {
    return `${getPatientName(r)} · ${getPatientMobile(r)}`;
  };
  const getAppointmentType = (r) => {
    const raw = (r.booking_channel || r.appointment_type || "").toUpperCase();
    if (raw.includes("PHONE")) return "Phone";
    if (raw.includes("ONLINE") || raw.includes("VIDEO")) return "Online";
    return "Not captured";
  };
  const uniqueOptions = (getter) => [...new Set(reports.map(getter).filter(v => v && v !== "Not captured"))].sort((a, b) => a.localeCompare(b));
  const cityOptions = uniqueOptions(r => r.city);
  const centerOptions = uniqueOptions(r => r.medical_center || r.clinic_name || r.clinic_address);
  const channelOptions = [...new Set(reports.map(getAppointmentType).filter(v => v !== "Not captured"))].sort((a, b) => a.localeCompare(b));

  const handleExport = () => {
    const rows = filteredReports;
    if (!rows.length) return;
    const headers = [
      "Practice Id",
      "Connection Id",
      "Date & Time",
      "Doctor & Center or Clinic",
      "Patient Name with Mobile Number",
      "Appointment Type",
      "Appointment Status",
      "PFC",
      "Dispute Status",
      "Comment",
    ];
    const lines = rows.map(r => [
      valueOrPlaceholder(r.practice_id),
      valueOrPlaceholder(r.connection_id),
      formatReportDateTime(r),
      formatDoctorCenter(r),
      formatPatientMobile(r),
      getAppointmentType(r),
      valueOrPlaceholder(r.status),
      formatCurrency(r.pfc ?? r.fee_amount),
      valueOrPlaceholder(r.dispute_status),
      valueOrPlaceholder(r.comment),
    ].map(v => `"${String(v).replaceAll('"', '""')}"`).join(","));
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `doctor-appointment-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusColor = {
    COMPLETED: { bg: "#EAF3DE", text: "#3B6D11" },
    CONFIRMED: { bg: "#E1F5EE", text: "#0F6E56" },
    CANCELLED: { bg: "#FCEBEB", text: "#A32D2D" },
    PENDING:   { bg: "#E8F1FA", text: "#0F4C81" },
    NO_SHOW:   { bg: "#FAEEDA", text: "#854F0B" },
    CHECKED_IN:{ bg: "#E1F5EE", text: "#0F6E56" },
  };

  const filteredReports = reports.filter(r => {
    const center = r.medical_center || r.clinic_name || r.clinic_address || "";
    return (!cityFilter || r.city === cityFilter)
      && (!centerFilter || center === centerFilter)
      && (!channelFilter || getAppointmentType(r) === channelFilter)
      && (!statusFilter || r.status === statusFilter);
  });
  const totalPages = Math.ceil(filteredReports.length / PAGE_SIZE);
  const pagedReports = filteredReports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="page" style={{ maxWidth: 1280 }}>

      {/* Header row with Export button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>Appointment Reports</h1>
          <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Appointment activity, patient charges, and booking source details</p>
        </div>
        <button onClick={handleExport} disabled={!filteredReports.length} style={{
          padding: "9px 18px", background: filteredReports.length ? C.accent : C.gray200,
          color: filteredReports.length ? C.white : C.gray400,
          border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600,
          cursor: filteredReports.length ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6,
        }}>
          ↓ Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
      <div className="responsive-grid stats-grid-3" style={{ marginBottom: 28 }}>
          <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.primary }}>{summary.total_appointments}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Appointments</div>
          </div>
          <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.accent }}>{summary.completed_appointments}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Completed</div>
          </div>
          <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.success }}>₹{summary.total_earnings.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Earnings</div>
          </div>
        </div>
      )}

      {/* Filters row */}
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <ReportDateInput label="From" value={from} onChange={setFrom} />
        <ReportDateInput label="To" value={to} onChange={setTo} />
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 6 }}>City</label>
          <select value={cityFilter} onChange={e => { setCityFilter(e.target.value); setPage(1); }}
            style={{ minWidth: 140, padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, background: C.white, color: C.gray800 }}>
            <option value="">All Cities</option>
            {cityOptions.map(city => <option key={city} value={city}>{city}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 6 }}>Medical Center / Clinic</label>
          <select value={centerFilter} onChange={e => { setCenterFilter(e.target.value); setPage(1); }}
            style={{ minWidth: 190, padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, background: C.white, color: C.gray800 }}>
            <option value="">All Centers</option>
            {centerOptions.map(center => <option key={center} value={center}>{center}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 6 }}>Booking Channel</label>
          <select value={channelFilter} onChange={e => { setChannelFilter(e.target.value); setPage(1); }}
            style={{ minWidth: 150, padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, background: C.white, color: C.gray800 }}>
            <option value="">All Channels</option>
            {channelOptions.map(channel => <option key={channel} value={channel}>{channel}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 6 }}>Status</label>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, background: C.white, color: C.gray800 }}>
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="CHECKED_IN">Checked In</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="NO_SHOW">No Show</option>
          </select>
        </div>
        <button onClick={() => fetchReports(null, from, to)} style={{
          padding: "8px 20px", background: C.primary, color: C.white, border: "none",
          borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>Apply</button>
        <button onClick={() => {
          setFrom(today); setTo(today); setCityFilter(""); setCenterFilter(""); setChannelFilter(""); setStatusFilter(""); setPage(1); fetchReports(doctorId, today, today);
        }} style={{
          padding: "8px 16px", background: "transparent", color: C.gray400, border: `1.5px solid ${C.border}`,
          borderRadius: 8, fontSize: 13, cursor: "pointer",
        }}>Clear</button>
      </div>

      {/* Table */}
      {loading ? <Spinner /> : error ? <Alert message={error} /> : filteredReports.length === 0 ? (
        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: 48, textAlign: "center" }}>
          <p style={{ color: C.gray400, fontSize: 14 }}>No appointments found.</p>
        </div>
      ) : (
        <>
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 1280, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.gray50, borderBottom: `1px solid ${C.border}` }}>
                  {["Practice Id", "Connection Id", "Date & Time", "Doctor & Center / Clinic", "Patient & Mobile", "Appointment Type", "Appointment Status", "PFC", "Dispute Status", "Comment"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: C.gray600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedReports.map((r, i) => {
                  const sc = statusColor[r.status] || { bg: C.gray100, text: C.gray600 };
                  return (
                    <tr key={r.appointment_id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.gray50 }}>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray800, fontWeight: 600 }}>{valueOrPlaceholder(r.practice_id)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray600 }}>{valueOrPlaceholder(r.connection_id)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray600, whiteSpace: "nowrap" }}>{formatReportDateTime(r)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, minWidth: 220 }}>
                        <div style={{ color: C.gray800, fontWeight: 600 }}>{getDoctorName(r)}</div>
                        <div style={{ color: C.primaryMid, fontSize: 11, marginTop: 3 }}>{getCenterName(r)}</div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, minWidth: 190 }}>
                        <div style={{ color: C.gray800, fontWeight: 600 }}>{getPatientName(r)}</div>
                        <div style={{ color: C.primaryMid, fontSize: 11, marginTop: 3 }}>{getPatientMobile(r)}</div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray600 }}>{getAppointmentType(r)}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase" }}>{r.status}</span>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 600, color: C.accent }}>
                        {formatCurrency(r.pfc ?? r.fee_amount)}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray600 }}>{valueOrPlaceholder(r.dispute_status)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: C.gray600, minWidth: 170 }}>{valueOrPlaceholder(r.comment)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── NEW: Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
              <span style={{ fontSize: 13, color: C.gray400 }}>
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredReports.length)} of {filteredReports.length}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{
                  padding: "6px 14px", borderRadius: 8, fontSize: 13, cursor: page === 1 ? "not-allowed" : "pointer",
                  background: C.white, color: page === 1 ? C.gray400 : C.primary,
                  border: `1.5px solid ${page === 1 ? C.gray200 : C.primary}`, fontWeight: 600,
                }}>← Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{
                  padding: "6px 14px", borderRadius: 8, fontSize: 13, cursor: page === totalPages ? "not-allowed" : "pointer",
                  background: C.white, color: page === totalPages ? C.gray400 : C.primary,
                  border: `1.5px solid ${page === totalPages ? C.gray200 : C.primary}`, fontWeight: 600,
                }}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
// ─── ADMIN DASHBOARD ──────────────────────────────────────────
function AdminDashboard({ token, user }) {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(() => formatDateInput(new Date()));
  const [scheduleMode, setScheduleMode] = useState("day");
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/doctors", token);
        setDoctors(data.data || []);
      } catch (e) {}
      setLoading(false);
    })();
  }, [token]);

  const fetchAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    try {
      const range = getScheduleRange(scheduleMode, date);
      const query = scheduleMode === "day"
        ? `date=${date}`
        : `from=${range.from}&to=${range.to}`;
      const data = await api.get(`/appointments/admin?${query}`, token);
      setAppointments(data.data || []);
    } catch (e) {
      setAppointments([]);
    }
    setAppointmentsLoading(false);
  }, [date, scheduleMode, token]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);
  const openScheduleDay = (selectedDate) => {
    setDate(selectedDate);
    setScheduleMode("day");
  };

  return (
    <div className="page" style={{ maxWidth: 1040 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>Admin Dashboard</h1>
        <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Hospital Appointment System Overview</p>
      </div>
      <div className="responsive-grid stats-grid-3" style={{ marginBottom: 28 }}>
        {[
          { label: "Total Doctors", value: doctors.length, color: C.primary },
          { label: "Active Today", value: "—", color: C.accent },
          { label: "System Status", value: "Live", color: C.success },
        ].map(s => (
          <div key={s.label} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 20px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: C.gray400, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="responsive-grid two-col-grid" style={{ gap: 12, marginBottom: 28 }}>
        <Card onClick={() => navigate("/admin/walkin")} style={{ cursor: "pointer", background: C.primaryLight, border: `1px solid rgba(15,76,129,0.2)` }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>→</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.primary }}>Register Walk-in</div>
          <div style={{ fontSize: 12, color: C.primaryMid, marginTop: 4 }}>Add patient to queue instantly</div>
        </Card>
        <Card onClick={() => navigate("/admin/notifications")} style={{ cursor: "pointer", background: C.accentLight, border: `1px solid rgba(15,110,86,0.2)` }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>◎</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.accent }}>Send Notification</div>
          <div style={{ fontSize: 12, color: C.accent, marginTop: 4 }}>SMS or Email reminders</div>
        </Card>
      </div>
      <div style={{ marginBottom: 28 }}>
        <div className="toolbar-row" style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: C.gray800, margin: 0 }}>
            {scheduleMode === "day" ? "Appointments" : scheduleMode === "week" ? "Weekly Schedule" : "Monthly Schedule"}
          </h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <ScheduleModeControl value={scheduleMode} onChange={setScheduleMode} />
            <div style={{ width: 180 }}>
              <Input label="" type="date" value={date} onChange={setDate} />
            </div>
          </div>
        </div>
        {scheduleMode !== "day" && !appointmentsLoading && (
            <ScheduleCountCalendar
              mode={scheduleMode}
              date={date}
              appointments={appointments}
              onSelectDate={openScheduleDay}
            />
        )}
        {appointmentsLoading ? <Spinner /> : appointments.length === 0 ? (
          <Card style={{ textAlign: "center", padding: 34 }}>
            <p style={{ color: C.gray400, fontSize: 14, margin: 0 }}>
              {scheduleMode === "day" ? "No appointments for this date." : "No appointments for this period."}
            </p>
          </Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {appointments.map(a => (
              <Card key={a.appointment_id} className="admin-appointment-row" style={{}}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.gray800 }}>{a.patient_name || "Patient"}</div>
                  <div style={{ fontSize: 12, color: C.primaryMid, marginTop: 3 }}>{formatDisplayPhone(a.contact_number) || "Mobile not captured"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>{a.doctor_name || "Doctor"}</div>
                  <div style={{ fontSize: 12, color: C.gray400, marginTop: 3 }}>{a.specialization || "General"} · {a.appointment_type || "Appointment"}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>{formatTime12(a.start_time)}</div>
                  {scheduleMode !== "day" && (
                    <div style={{ fontSize: 11, color: C.gray400, marginTop: 3 }}>
                      {formatDisplayDate(a.appointment_date, { day: "numeric", month: "short" })}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center", marginTop: 5 }}>
                    <Badge color={APPOINTMENT_STATUS_BADGE[a.status] || "gray"}>{a.status || "Pending"}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: C.gray800, marginBottom: 16 }}>Registered Doctors</h2>
      {loading ? <Spinner /> : (
        <div className="responsive-grid two-col-grid" style={{ gap: 12 }}>
          {doctors.map(d => (
            <Card key={d.doctor_id}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: C.primary, flexShrink: 0 }}>{d.name?.charAt(0)}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.gray800 }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: C.gray400 }}>{d.specialization || "General"}</div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>₹{d.consultation_fee || "—"}</div>
                  <div style={{ fontSize: 11, color: C.gray400 }}>{d.experience_years ? `${d.experience_years} yrs` : ""}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── WALK-IN & NOTIFICATIONS — kept from original (unchanged logic) ───
function WalkIn({ token, user }) {
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [staffScope, setStaffScope] = useState(null);
  const [form, setForm] = useState({
    doctor_id: "",
    appointment_date: formatDateInput(new Date()),
    start_time: "",
    patient_name: "",
    reason_for_visit: "",
    contact_number: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const role = normalizeRole(user?.role);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const profile = await api.get("/auth/me", token);
        const scope = profile.data?.staff || null;
        setStaffScope(scope);
        if (role !== "ADMIN" && !scope?.clinic_name && !scope?.clinic_address) {
          setDoctors([]);
          return;
        }
        const params = new URLSearchParams({ page: "1", limit: "100" });
        if (scope?.clinic_name) params.append("clinic_name", scope.clinic_name);
        if (scope?.clinic_address) params.append("clinic_address", scope.clinic_address);
        const data = await api.get(`/doctors?${params.toString()}`, token);
        setDoctors(data.data || []);
      } catch (e) {
        setDoctors([]);
      }
    })();
  }, [token, role]);

  useEffect(() => {
    if (!form.doctor_id || !form.appointment_date) {
      setSlots([]);
      set("start_time", "");
      return;
    }
    (async () => {
      setSlotsLoading(true);
      try {
        const data = await api.get(`/availability/slots?doctor_id=${form.doctor_id}&date=${form.appointment_date}`, token);
        const available = (data.available_slots || []).filter(slot => !slot.is_booked && !slot.is_blocked);
        setSlots(available);
        setForm(current => ({
          ...current,
          start_time: available.some(slot => slot.start_time === current.start_time) ? current.start_time : "",
        }));
      } catch (e) {
        setSlots([]);
        setForm(current => ({ ...current, start_time: "" }));
      }
      setSlotsLoading(false);
    })();
  }, [form.doctor_id, form.appointment_date, token]);

  const handleSubmit = async () => {
    if (!form.doctor_id || !form.appointment_date || !form.start_time || !form.patient_name || !form.reason_for_visit) {
      return setError("Doctor, date, time slot, patient name and reason are required.");
    }
    setError(""); setLoading(true);
    try {
      const data = await api.post("/appointments/walkin", { ...form, doctor_id: parseInt(form.doctor_id) }, token);
      setSuccess(data.data);
    } catch (e) { setError(e.message || "Failed to register walk-in."); }
    setLoading(false);
  };

  if (success) return (
    <div className="page" style={{ maxWidth: 500 }}>
      <Card style={{ textAlign: "center", padding: 48 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.success, margin: "0 0 8px" }}>Walk-in Registered!</h2>
        <p style={{ color: C.gray400, fontSize: 14, margin: "0 0 8px" }}>Queue position: <strong>{success.queue_position}</strong></p>
        <p style={{ color: C.gray400, fontSize: 14, margin: "0 0 24px" }}>Est. wait: {success.estimated_wait_minutes} minutes</p>
        <Button variant="success" style={{ marginTop: 10 }} onClick={() => setSuccess(null)}>Register Another</Button>
      </Card>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 500 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: "0 0 24px", fontFamily: "'Playfair Display', serif" }}>Register Walk-in</h1>
      <Card>
        <Alert message={error} type="error" />
        {staffScope?.clinic_name && (
          <Alert type="info" message={`Showing doctors for ${staffScope.clinic_name}.`} />
        )}
        {!staffScope?.clinic_name && ["ADMIN", "MEDICAL_ADMIN", "RECEPTIONIST"].includes(String(user?.role || "").toUpperCase()) && (
          <Alert type="info" message={role === "ADMIN" ? "No clinic is assigned to this admin profile, so the system-wide doctor list is available." : "No clinic is assigned to this staff profile yet. Set the clinic in My Profile before registering walk-ins."} />
        )}
        <Input label="Appointment Date" type="date" value={form.appointment_date} onChange={v => set("appointment_date", v)} required />
        <Input label="Doctor" type="select" value={form.doctor_id} onChange={v => set("doctor_id", v)}
          options={[{ value: "", label: "Select doctor..." }, ...doctors.map(d => ({ value: d.doctor_id, label: `${d.name} — ${d.specialization || "General"}${d.clinic_name ? ` — ${d.clinic_name}` : ""}` }))]} required />
        <Input
          label="Available Time Slot"
          type="select"
          value={form.start_time}
          onChange={v => set("start_time", v)}
          options={[
            { value: "", label: slotsLoading ? "Loading slots..." : slots.length === 0 ? "No available slots" : "Select time..." },
            ...slots.map(slot => ({ value: slot.start_time, label: `${formatTime12(slot.start_time)} - ${formatTime12(slot.end_time)}` })),
          ]}
          required
        />
        <Input label="Patient Name" value={form.patient_name} onChange={v => set("patient_name", v)} placeholder="Full name" required />
        <Input label="Reason for Visit" type="textarea" value={form.reason_for_visit} onChange={v => set("reason_for_visit", v)} placeholder="Brief description..." required />
        <Input label="Contact Number" value={form.contact_number} onChange={v => set("contact_number", v)} placeholder="+91 98765 43210" />
        <Button onClick={handleSubmit} disabled={loading || !form.start_time} size="lg" style={{ width: "100%" }}>{loading ? "Registering..." : "Register Walk-in"}</Button>
      </Card>
    </div>
  );
}

function SendNotification({ token }) {
  const [form, setForm] = useState({ appointment_id: "", channel: "SMS", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSend = async () => {
    if (!form.appointment_id) return setError("Appointment ID is required.");
    setError(""); setLoading(true);
    try {
      const data = await api.post("/notifications/reminder", { appointment_id: parseInt(form.appointment_id), channel: form.channel, message: form.message || undefined }, token);
      setSuccess(data);
    } catch (e) { setError(e.message || "Failed to send reminder."); }
    setLoading(false);
  };

  if (success) return (
    <div className="page" style={{ maxWidth: 500 }}>
      <Card style={{ textAlign: "center", padding: 48 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.success, margin: "0 0 8px" }}>Reminder Sent!</h2>
        <p style={{ color: C.gray400, fontSize: 14, margin: "0 0 24px" }}>{success.message}</p>
        <Button size="sm" variant="success" style={{ marginTop: 10 }} onClick={() => setSuccess(null)}>Send Another</Button>
      </Card>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 500 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: "0 0 24px", fontFamily: "'Playfair Display', serif" }}>Send Notification</h1>
      <Card>
        <Alert message={error} type="error" />
        <Input label="Appointment ID" type="number" value={form.appointment_id} onChange={v => set("appointment_id", v)} placeholder="Enter appointment ID" required />
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 8 }}>Channel *</label>
          <div style={{ display: "flex", gap: 10 }}>
            {["SMS", "EMAIL", "BOTH"].map(ch => (
              <button key={ch} onClick={() => set("channel", ch)} style={{
                flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, cursor: "pointer",
                background: form.channel === ch ? C.primary : C.white, color: form.channel === ch ? C.white : C.gray600,
                border: `1.5px solid ${form.channel === ch ? C.primary : C.border}`,
                fontWeight: form.channel === ch ? 600 : 400, transition: "all 0.15s",
              }}>{ch}</button>
            ))}
          </div>
        </div>
        <Input label="Custom Message (optional)" type="textarea" value={form.message} onChange={v => set("message", v)} placeholder="Leave blank to use default reminder message..." />
        <Button onClick={handleSend} disabled={loading || !form.appointment_id} size="lg" style={{ width: "100%" }}>
          {loading ? "Sending..." : `Send ${form.channel} Reminder`}
        </Button>
      </Card>
    </div>
  );
}

const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

function formatDateInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getRangeFromAnchor(mode, anchorDate) {
  const anchor = new Date(`${anchorDate}T00:00:00`);
  if (mode === "week") {
    const start = new Date(anchor);
    start.setDate(anchor.getDate() - anchor.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start_date: formatDateInput(start), end_date: formatDateInput(end) };
  }
  if (mode === "month") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start_date: formatDateInput(start), end_date: formatDateInput(end) };
  }
  return { start_date: anchorDate, end_date: anchorDate };
}

function countSelectedDates(startDate, endDate, weekdays) {
  const selected = new Set(weekdays.map(Number));
  const current = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  let count = 0;
  while (current <= end) {
    if (selected.size === 0 || selected.has(current.getDay())) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

// ─── SET AVAILABILITY ─────────────────────────────────────────
function SetAvailability({ token, user }) {
  const [activeTab, setActiveTab] = useState("availability");
  const [editingBlock, setEditingBlock] = useState(null);
  const [availabilityMode, setAvailabilityMode] = useState("single");
  const [availabilityWeekdays, setAvailabilityWeekdays] = useState([1, 2, 3, 4, 5]);
  const [blockMode, setBlockMode] = useState("single");
  const [blockWeekdays, setBlockWeekdays] = useState([1, 2, 3, 4, 5]);
  const [doctorId, setDoctorId] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], start_time: "09:00", end_time: "17:00", slot_duration: "30" });
  const [blockForm, setBlockForm] = useState({
    block_date: new Date().toISOString().split("T")[0],
    start_time: "12:00",
    end_time: "13:00",
    reason: "Lunch Break",
  });
  const [holidayForm, setHolidayForm] = useState({
    date: new Date().toISOString().split("T")[0],
    name: "",
  });
  const [holidayList, setHolidayList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [blockSuccess, setBlockSuccess] = useState("");
  const [blockError, setBlockError] = useState("");
  const [holidaySuccess, setHolidaySuccess] = useState("");
  const [holidayError, setHolidayError] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [blocksDate, setBlocksDate] = useState(new Date().toISOString().split("T")[0]);
  const [doctors, setDoctors] = useState([]);
  const [staffScope, setStaffScope] = useState(null);
  const role = normalizeRole(user?.role);
  const canSelectDoctor = isStaffRole(role);

  useEffect(() => {
    (async () => {
      try {
        let doctorsPath = "/doctors?page=1&limit=100";
        if (canSelectDoctor) {
          const profile = await api.get("/auth/me", token);
          const scope = profile.data?.staff || null;
          setStaffScope(scope);
          if (role !== "ADMIN" && !scope?.clinic_name && !scope?.clinic_address) {
            setDoctors([]);
            setDoctorId(null);
            return;
          }
          const params = new URLSearchParams();
          if (scope?.clinic_name) params.set("clinic_name", scope.clinic_name);
          if (scope?.clinic_address) params.set("clinic_address", scope.clinic_address);
          if (params.toString()) doctorsPath += `&${params.toString()}`;
        }
        const data = await api.get(doctorsPath, token);
        const list = data.data || [];
        setDoctors(list);
        if (canSelectDoctor) {
          setDoctorId(current => current || list[0]?.doctor_id || null);
        } else {
          const me = list.find(d => d.email === user.email);
          if (me) setDoctorId(me.doctor_id);
        }
      } catch (e) {}
    })();
  }, [token, user, canSelectDoctor, role]);

  const fetchBlocks = useCallback(async () => {
    if (!doctorId) return;
    try {
      const data = await api.get(`/availability/blocks/${doctorId}?date=${blocksDate}`, token);
      setBlocks(data.data || []);
    } catch (e) { setBlocks([]); }
  }, [doctorId, blocksDate, token]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBlock = (k, v) => setBlockForm(f => ({ ...f, [k]: v }));
  const setHoliday = (k, v) => setHolidayForm(f => ({ ...f, [k]: v }));
  const toggleWeekday = (value, setter) => {
    setter(days => days.includes(value) ? days.filter(d => d !== value) : [...days, value].sort());
  };

  const handleSubmit = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
      const data = availabilityMode === "single"
        ? await api.post("/availability", {
            doctor_id: doctorId,
            date: form.date,
            start_time: form.start_time,
            end_time: form.end_time,
            slot_duration: parseInt(form.slot_duration),
          }, token)
        : await api.post("/availability/bulk", {
            doctor_id: doctorId,
            ...getRangeFromAnchor(availabilityMode, form.date),
            weekdays: availabilityWeekdays,
            start_time: form.start_time,
            end_time: form.end_time,
            slot_duration: parseInt(form.slot_duration),
          }, token);
      setSuccess(data.message || "Availability created successfully!");
    } catch (e) { setError(e.message || "Failed to create availability."); }
    setLoading(false);
  };

  const handleBlockSubmit = async () => {
    setBlockError(""); setBlockSuccess(""); setBlockLoading(true);
    try {
      const body = {
        block_date: blockForm.block_date,
        start_time: blockForm.start_time,
        end_time: blockForm.end_time,
        reason: blockForm.reason,
      };

      if (editingBlock?.block_id) {
        await api.patch(`/availability/block/${editingBlock.block_id}`, body, token);
        setBlockSuccess(`Time block updated to ${blockForm.start_time} - ${blockForm.end_time}.`);
      } else if (blockMode !== "single") {
        const data = await api.post("/availability/block/bulk", {
          doctor_id: doctorId,
          ...getRangeFromAnchor(blockMode, blockForm.block_date),
          weekdays: blockWeekdays,
          start_time: blockForm.start_time,
          end_time: blockForm.end_time,
          reason: blockForm.reason,
        }, token);
        setBlockSuccess(data.message || "Time blocked successfully.");
      } else {
        await api.post("/availability/block", { doctor_id: doctorId, ...body }, token);
        setBlockSuccess(`Time blocked from ${blockForm.start_time} to ${blockForm.end_time}!`);
      }

      setEditingBlock(null);
      setBlocksDate(blockForm.block_date);
      await fetchBlocks();
    } catch (e) { setBlockError(e.message || "Failed to block time."); }
    setBlockLoading(false);
  };

  const addHolidayToList = () => {
    if (!holidayForm.date) return;
    setHolidayList(list => [...list, { date: holidayForm.date, name: holidayForm.name || "Public Holiday" }]);
    setHolidayForm(f => ({ ...f, name: "" }));
    setHolidayError("");
  };

  const handleHolidaySubmit = async () => {
    setHolidayError(""); setHolidaySuccess(""); setHolidayLoading(true);
    try {
      const holidays = holidayList.length > 0
        ? holidayList
        : [{ date: holidayForm.date, name: holidayForm.name || "Public Holiday" }];
      const data = await api.post("/availability/holidays", { doctor_id: doctorId, holidays }, token);
      setHolidaySuccess(data.message || "Public holidays marked.");
      setHolidayList([]);
      setBlocksDate(holidays[0]?.date || blocksDate);
      await fetchBlocks();
    } catch (e) { setHolidayError(e.message || "Failed to mark public holidays."); }
    setHolidayLoading(false);
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setBlockForm({
      block_date: block.block_date?.substring(0, 10) || blocksDate,
      start_time: block.start_time?.substring(0, 5) || "12:00",
      end_time: block.end_time?.substring(0, 5) || "13:00",
      reason: block.reason || "Other",
    });
    setBlocksDate(block.block_date?.substring(0, 10) || blocksDate);
    setBlockError("");
    setBlockSuccess("");
  };

  const handleCancelEditBlock = () => {
    setEditingBlock(null);
    setBlockForm({
      block_date: blocksDate,
      start_time: "12:00",
      end_time: "13:00",
      reason: "Lunch Break",
    });
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm("Remove this time block?")) return;
    try {
      await api.request("DELETE", `/availability/block/${blockId}`, null, token);
      await fetchBlocks();
    } catch (e) { alert("Failed to remove block."); }
  };

  const slotsCount = () => {
    if (!form.start_time || !form.end_time || !form.slot_duration) return 0;
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    return Math.floor(((eh * 60 + em) - (sh * 60 + sm)) / parseInt(form.slot_duration));
  };

  const availabilityRange = getRangeFromAnchor(availabilityMode, form.date);
  const availabilityDays = availabilityMode === "single" ? 1 : countSelectedDates(availabilityRange.start_date, availabilityRange.end_date, availabilityWeekdays);
  const blockRange = getRangeFromAnchor(blockMode, blockForm.block_date);
  const blockDays = blockMode === "single" || editingBlock ? 1 : countSelectedDates(blockRange.start_date, blockRange.end_date, blockWeekdays);
  const BLOCK_REASONS = ["Lunch Break", "Meeting", "Personal", "Emergency", "Training", "Other"];

  return (
    <div className="page" style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>Schedule Management</h1>
        <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>Set availability and block time off</p>
      </div>

      {canSelectDoctor && (
        <Card style={{ marginBottom: 20 }}>
          {staffScope?.clinic_name && (
            <Alert message={`Showing doctors assigned to ${staffScope.clinic_name}.`} type="success" />
          )}
          {!staffScope?.clinic_name && role !== "ADMIN" && (
            <Alert message="Your profile is not linked to a clinic yet. Add a clinic in My Profile before managing schedules." type="warning" />
          )}
          <Input label="Doctor" type="select" value={doctorId || ""} onChange={v => setDoctorId(v ? parseInt(v) : null)}
            options={[
              { value: "", label: doctors.length ? "Select doctor..." : "No doctors available" },
              ...doctors.map(d => ({ value: d.doctor_id, label: `${d.name} — ${d.specialization || "General"}${d.clinic_name ? ` — ${d.clinic_name}` : ""}` })),
            ]} required />
        </Card>
      )}

      <div style={{ display: "flex", background: C.gray100, borderRadius: 10, padding: 3, marginBottom: 24 }}>
        {[
          { id: "availability", label: "Set Availability" },
          { id: "block", label: "Block Time" },
          { id: "holidays", label: "Public Holidays" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
            background: activeTab === tab.id ? C.white : "transparent",
            color: activeTab === tab.id ? C.primary : C.gray400,
            fontWeight: activeTab === tab.id ? 600 : 400,
            fontSize: 13, cursor: "pointer",
            boxShadow: activeTab === tab.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
            transition: "all 0.15s",
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "availability" && (
        <Card>
          <Alert message={error} type="error" />
          <Alert message={success} type="success" />
          <Input label="Apply To" type="select" value={availabilityMode} onChange={setAvailabilityMode}
            options={[
              { value: "single", label: "One day" },
              { value: "week", label: "Week of selected date" },
              { value: "month", label: "Month of selected date" },
            ]} />
          <Input label={availabilityMode === "single" ? "Date" : "Selected Date"} type="date" value={form.date} onChange={v => set("date", v)} required />
          {availabilityMode !== "single" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 8 }}>Days</label>
              <div className="weekday-grid">
                {WEEKDAYS.map(day => {
                  const selected = availabilityWeekdays.includes(day.value);
                  return (
                    <button key={day.value} onClick={() => toggleWeekday(day.value, setAvailabilityWeekdays)} style={{
                      padding: "8px 0",
                      borderRadius: 8,
                      border: `1.5px solid ${selected ? C.primary : C.border}`,
                      background: selected ? C.primaryLight : C.white,
                      color: selected ? C.primary : C.gray600,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}>{day.label}</button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="responsive-grid form-grid-2">
            <Input label="Start Time" type="time" value={form.start_time} onChange={v => set("start_time", v)} required />
            <Input label="End Time" type="time" value={form.end_time} onChange={v => set("end_time", v)} required />
          </div>
          <Input label="Slot Duration" type="select" value={form.slot_duration} onChange={v => set("slot_duration", v)}
            options={[
              { value: "15", label: "15 minutes" },
              { value: "30", label: "30 minutes" },
              { value: "45", label: "45 minutes" },
              { value: "60", label: "60 minutes" },
            ]} />
          <div style={{ background: C.primaryLight, borderRadius: 8, padding: "14px 16px", marginBottom: 20, border: `1px solid rgba(15,76,129,0.15)` }}>
            <p style={{ fontSize: 13, color: C.primary, fontWeight: 600, margin: "0 0 4px" }}>Preview</p>
            <p style={{ fontSize: 13, color: C.primaryMid, margin: 0 }}>
              {availabilityDays} day(s) · {slotsCount()} slots per day · {availabilityDays * slotsCount()} total slots · {form.start_time} to {form.end_time}
              {availabilityMode !== "single" ? ` · ${availabilityRange.start_date} to ${availabilityRange.end_date}` : ` · ${new Date(form.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}`}
            </p>
          </div>
          <Button onClick={handleSubmit} disabled={loading || !doctorId || (availabilityMode !== "single" && availabilityWeekdays.length === 0)} size="lg" style={{ width: "100%" }}>
            {loading ? "Creating..." : availabilityMode === "single" ? "Create Availability" : "Create Availability Range"}
          </Button>
        </Card>
      )}

      {activeTab === "block" && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <Alert message={blockError} type="error" />
            <Alert message={blockSuccess} type="success" />
            {editingBlock && (
              <div style={{ background: C.primaryLight, border: `1px solid rgba(15,76,129,0.15)`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: C.primary, fontWeight: 700, margin: 0 }}>Editing blocked time</p>
              </div>
            )}
            {!editingBlock && (
              <Input label="Apply To" type="select" value={blockMode} onChange={setBlockMode}
                options={[
                  { value: "single", label: "One day" },
                  { value: "week", label: "Week of selected date" },
                  { value: "month", label: "Month of selected date" },
                ]} />
            )}
            <Input label={blockMode === "single" || editingBlock ? "Date" : "Selected Date"} type="date" value={blockForm.block_date} onChange={v => setBlock("block_date", v)} required />
            {blockMode !== "single" && !editingBlock && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.gray800, display: "block", marginBottom: 8 }}>Days</label>
                <div className="weekday-grid">
                  {WEEKDAYS.map(day => {
                    const selected = blockWeekdays.includes(day.value);
                    return (
                      <button key={day.value} onClick={() => toggleWeekday(day.value, setBlockWeekdays)} style={{
                        padding: "8px 0",
                        borderRadius: 8,
                        border: `1.5px solid ${selected ? C.warning : C.border}`,
                        background: selected ? C.warningLight : C.white,
                        color: selected ? C.warning : C.gray600,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}>{day.label}</button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="responsive-grid form-grid-2">
              <Input label="Start Time" type="time" value={blockForm.start_time} onChange={v => setBlock("start_time", v)} required />
              <Input label="End Time" type="time" value={blockForm.end_time} onChange={v => setBlock("end_time", v)} required />
            </div>
            <Input label="Reason" type="select" value={blockForm.reason} onChange={v => setBlock("reason", v)}
              options={BLOCK_REASONS.map(r => ({ value: r, label: r }))} />

            <div style={{ background: C.warningLight, borderRadius: 8, padding: "10px 14px", marginBottom: 16, border: `1px solid #FAC775` }}>
              <p style={{ fontSize: 12, color: C.warning, margin: 0 }}>
                {blockDays} day(s) will be blocked. Existing bookings in this range are not affected.
                {blockMode !== "single" && !editingBlock ? ` Range: ${blockRange.start_date} to ${blockRange.end_date}.` : ""}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {editingBlock && (
                <Button onClick={handleCancelEditBlock} disabled={blockLoading} size="lg" style={{ flex: 1 }} variant="secondary">
                  Cancel
                </Button>
              )}
              <Button onClick={handleBlockSubmit} disabled={blockLoading || !doctorId || (blockMode !== "single" && !editingBlock && blockWeekdays.length === 0)} size="lg" style={{ flex: 1 }} variant="warning">
                {blockLoading ? "Saving..." : editingBlock ? "Save Block" : "Block This Time"}
              </Button>
            </div>
          </Card>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: C.gray800, margin: 0 }}>Blocked Times</h2>
              <input type="date" value={blocksDate} onChange={e => setBlocksDate(e.target.value)}
                style={{ padding: "6px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13 }} />
            </div>
            {blocks.length === 0 ? (
              <div style={{ background: C.gray50, borderRadius: 10, padding: 24, textAlign: "center", border: `1px solid ${C.border}` }}>
                <p style={{ color: C.gray400, fontSize: 13 }}>No blocked times for this date.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {blocks.map(b => (
                  <div key={b.block_id} style={{ background: C.warningLight, border: `1px solid #FAC775`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.warning }}>
                        {b.start_time?.substring(0, 5)} - {b.end_time?.substring(0, 5)}
                      </div>
                      <div style={{ fontSize: 12, color: C.warning, marginTop: 2 }}>{b.reason || "No reason"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => handleEditBlock(b)} style={{
                        background: C.white, border: `1px solid ${C.warning}`,
                        color: C.warning, borderRadius: 6, padding: "4px 10px",
                        fontSize: 12, cursor: "pointer", fontWeight: 600,
                      }}>Edit</button>
                      <button onClick={() => handleDeleteBlock(b.block_id)} style={{
                        background: "transparent", border: `1px solid ${C.danger}`,
                        color: C.danger, borderRadius: 6, padding: "4px 10px",
                        fontSize: 12, cursor: "pointer", fontWeight: 600,
                      }}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "holidays" && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <Alert message={holidayError} type="error" />
            <Alert message={holidaySuccess} type="success" />
            <Input label="Holiday Date" type="date" value={holidayForm.date} onChange={v => setHoliday("date", v)} required />
            <Input label="Holiday Name" value={holidayForm.name} onChange={v => setHoliday("name", v)} placeholder="e.g. Independence Day" />
            <div style={{ background: C.warningLight, borderRadius: 8, padding: "10px 14px", marginBottom: 16, border: `1px solid #FAC775` }}>
              <p style={{ fontSize: 12, color: C.warning, margin: 0 }}>
                Public holidays are saved as full-day blocks, so all slots on those dates become unavailable.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button onClick={addHolidayToList} disabled={!holidayForm.date || holidayLoading} size="lg" style={{ flex: 1 }} variant="secondary">
                Add to List
              </Button>
              <Button onClick={handleHolidaySubmit} disabled={holidayLoading || !doctorId || (!holidayForm.date && holidayList.length === 0)} size="lg" style={{ flex: 1 }} variant="warning">
                {holidayLoading ? "Marking..." : holidayList.length > 0 ? "Mark Listed Holidays" : "Mark Holiday"}
              </Button>
            </div>
          </Card>

          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: C.gray800, margin: "0 0 12px" }}>Holidays To Mark</h2>
            {holidayList.length === 0 ? (
              <div style={{ background: C.gray50, borderRadius: 10, padding: 24, textAlign: "center", border: `1px solid ${C.border}` }}>
                <p style={{ color: C.gray400, fontSize: 13 }}>Add multiple holidays here, or mark the selected holiday directly.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {holidayList.map((holiday, index) => (
                  <div key={`${holiday.date}-${index}`} style={{ background: C.warningLight, border: `1px solid #FAC775`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.warning }}>{holiday.date}</div>
                      <div style={{ fontSize: 12, color: C.warning, marginTop: 2 }}>{holiday.name || "Public Holiday"}</div>
                    </div>
                    <button onClick={() => setHolidayList(list => list.filter((_, i) => i !== index))} style={{
                      background: "transparent", border: `1px solid ${C.danger}`,
                      color: C.danger, borderRadius: 6, padding: "4px 10px",
                      fontSize: 12, cursor: "pointer", fontWeight: 600,
                    }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROFILE ─────────────────────────────────────────────────
function ProfilePage({ token, user, onUserUpdate }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    city: "",
    state: "",
    phone_number: "",
    age: "",
    gender: "",
    specialization: "",
    experience_years: "",
    consultation_fee: "",
    qualifications: "",
    about: "",
    clinic_name: "",
    clinic_address: "",
    profile_photo: "",
    designation: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const role = String(user?.role || "").toUpperCase();
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try {
        const data = await api.get("/auth/me", token);
        const profile = data.data || {};
        setForm(current => ({
          ...current,
          name: profile.name || "",
          email: profile.email || "",
          city: profile.city || "",
          state: profile.state || "",
          phone_number: profile.patient?.contact_number || "",
          age: profile.patient?.age || "",
          gender: profile.patient?.gender || "",
          specialization: profile.doctor?.specialization || "",
          experience_years: profile.doctor?.experience_years || "",
          consultation_fee: profile.doctor?.consultation_fee || "",
          qualifications: profile.doctor?.qualifications || "",
          about: profile.doctor?.about || "",
          clinic_name: profile.doctor?.clinic_name || profile.staff?.clinic_name || "",
          clinic_address: profile.doctor?.clinic_address || profile.staff?.clinic_address || "",
          profile_photo: profile.doctor?.profile_photo || "",
          designation: profile.doctor?.designation || "",
        }));
      } catch (e) {
        setError(e.message || "Could not load profile.");
      }
      setLoading(false);
    })();
  }, [token]);

  const handleSave = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = {
        name: form.name,
        email: form.email,
        city: form.city,
        state: form.state,
      };
      if (role === "PATIENT") {
        Object.assign(payload, {
          phone_number: form.phone_number,
          age: form.age,
          gender: form.gender,
        });
      }
      if (role === "DOCTOR") {
        Object.assign(payload, {
          specialization: form.specialization,
          experience_years: form.experience_years,
          consultation_fee: form.consultation_fee,
          qualifications: form.qualifications,
          about: form.about,
          clinic_name: form.clinic_name,
          clinic_address: form.clinic_address,
          profile_photo: form.profile_photo,
          designation: form.designation,
        });
      }
      if (isStaffRole(role)) {
        Object.assign(payload, {
          clinic_name: form.clinic_name,
          clinic_address: form.clinic_address,
        });
      }
      const data = await api.put("/auth/me", payload, token);
      const updated = data.data || {};
      onUserUpdate?.({
        ...user,
        user_id: updated.user_id || user.user_id,
        name: updated.name || user.name,
        email: updated.email || user.email,
        role: updated.role || user.role,
        city: updated.city || "",
        state: updated.state || "",
      });
      setSuccess("Profile updated successfully.");
    } catch (e) {
      setError(e.message || "Failed to update profile.");
    }
    setSaving(false);
  };

  if (loading) return <div className="page" style={{ maxWidth: 900 }}><Spinner /></div>;

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.gray800, margin: 0, fontFamily: "'Playfair Display', serif" }}>My Profile</h1>
        <p style={{ color: C.gray400, fontSize: 14, marginTop: 4 }}>View and edit your account information.</p>
      </div>
      <Alert message={error} type="error" />
      <Alert message={success} type="success" />
      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 18 }}>Account Details</h2>
        <div className="responsive-grid form-grid-2">
          <Input label="Name" value={form.name} onChange={v => set("name", v)} required />
          <Input label="Email" type="email" value={form.email} onChange={v => set("email", v)} required />
          <Input label="City" value={form.city} onChange={v => set("city", v)} />
          <Input label="State" value={form.state} onChange={v => set("state", v)} />
        </div>

        {role === "PATIENT" && (
          <div style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 18 }}>Patient Details</h2>
            <div className="responsive-grid form-grid-3">
              <Input label="Phone Number" value={form.phone_number} onChange={v => set("phone_number", v)} placeholder="+1-925-413-8648" required />
              <Input label="Age" type="number" value={form.age} onChange={v => set("age", v)} />
              <Input
                label="Gender"
                type="select"
                value={form.gender}
                onChange={v => set("gender", v)}
                options={[
                  { value: "", label: "Select..." },
                  { value: "MALE", label: "Male" },
                  { value: "FEMALE", label: "Female" },
                  { value: "OTHER", label: "Other" },
                ]}
              />
            </div>
          </div>
        )}

        {role === "DOCTOR" && (
          <div style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 18 }}>Doctor Details</h2>
            <div className="responsive-grid form-grid-2">
              <Input label="Designation" value={form.designation} onChange={v => set("designation", v)} placeholder="Dr., Consultant, Specialist" />
              <Input label="Specialization" type="select" value={form.specialization} onChange={v => set("specialization", v)}
                options={[{ value: "", label: "Select specialization..." }, ...SORTED_SPECIALIZATIONS.map(s => ({ value: s, label: s }))]} />
              <Input label="Experience Years" type="number" value={form.experience_years} onChange={v => set("experience_years", v)} />
              <Input label="Consultation Fee" type="number" value={form.consultation_fee} onChange={v => set("consultation_fee", v)} />
              <Input label="Clinic Name" value={form.clinic_name} onChange={v => set("clinic_name", v)} />
              <Input label="Profile Photo URL" value={form.profile_photo} onChange={v => set("profile_photo", v)} />
            </div>
            <Input label="Clinic Address" value={form.clinic_address} onChange={v => set("clinic_address", v)} />
            <Input label="Qualifications" value={form.qualifications} onChange={v => set("qualifications", v)} placeholder="MD, MBBS, Fellowship..." />
            <Input label="About" type="textarea" value={form.about} onChange={v => set("about", v)} placeholder="Short professional bio" />
          </div>
        )}

        {isStaffRole(role) && (
          <div style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 18 }}>Clinic Assignment</h2>
            <div className="responsive-grid form-grid-2">
              <Input label="Medical Center / Clinic Name" value={form.clinic_name} onChange={v => set("clinic_name", v)} placeholder="TrueKarma Medical Center" />
              <Input label="Medical Center / Clinic Address" value={form.clinic_address} onChange={v => set("clinic_address", v)} placeholder="Street, city, state" />
            </div>
            <p style={{ fontSize: 12, color: C.gray400, margin: "0 0 16px" }}>
              Receptionist and medical admin access is scoped to doctors with this exact clinic name and address.
            </p>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving || !form.name || !form.email || (role === "PATIENT" && !form.phone_number)} size="lg" style={{ width: "100%", marginTop: 8 }}>
          {saving ? "Saving..." : "Save Profile"}
        </Button>
      </Card>
    </div>
  );
}

// ─── PROTECTED ROUTE ──────────────────────────────────────────
function ProtectedRoute({ token, children }) {
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

// ─── MAIN APP ─────────────────────────────────────────────────
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("ayushya_token"));
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("ayushya_user");
    return u ? JSON.parse(u) : null;
  });

  const handleLogin = (t, u) => {
    setToken(t); setUser(u);
    localStorage.setItem("ayushya_token", t);
    localStorage.setItem("ayushya_user", JSON.stringify(u));
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("ayushya_user", JSON.stringify(updatedUser));
  };

  const handleLogout = () => {
    setToken(null); setUser(null);
    localStorage.removeItem("ayushya_token");
    localStorage.removeItem("ayushya_user");
  };

  return (
    <BrowserRouter>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #F8F9FA; }
        input, select, textarea, button { font-family: inherit; }
        .app-shell { display: flex; min-height: 100vh; }
        .app-sidebar { width: 220px; min-height: 100vh; display: flex; flex-direction: column; flex-shrink: 0; }
        .app-main { flex: 1; overflow-y: auto; background: #F8F9FA; min-width: 0; }
        .page { padding: 32px 36px; width: 100%; }
        .sidebar-brand { padding: 28px 20px 20px; }
        .sidebar-user { padding: 16px 20px; }
        .sidebar-avatar { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
        .sidebar-nav { padding: 12px; }
        .sidebar-nav-item { width: 100%; padding: 10px 12px; border-radius: 8px; border: none; cursor: pointer; text-align: left; font-size: 13px; display: flex; align-items: center; gap: 10px; transition: all 0.15s; margin-bottom: 2px; }
        .sidebar-signout { margin-top: 10px; }
        .responsive-grid { display: grid; gap: 16px; }
        .stats-grid-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .stats-grid-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .form-grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .form-grid-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .two-col-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .doctor-slot-row { display: grid; grid-template-columns: 116px 1fr 118px; gap: 16px; align-items: stretch; }
        .admin-appointment-row { display: grid; grid-template-columns: 1.3fr 1.2fr auto; gap: 16px; align-items: center; }
        .toolbar-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
        .weekday-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 8px; }
        .mobile-stack-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
        @media (max-width: 900px) {
          .app-shell { flex-direction: column; }
          .app-sidebar { width: 100%; min-height: auto; position: sticky; top: 0; z-index: 10; }
          .sidebar-brand { padding: 12px 14px; }
          .sidebar-brand img { max-width: 132px; }
          .sidebar-user { padding: 10px 14px; display: flex; align-items: center; gap: 10px; }
          .sidebar-avatar { width: 32px; height: 32px; margin-bottom: 0; flex: 0 0 auto; }
          .sidebar-nav { padding: 8px 10px 10px; display: flex; gap: 6px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .sidebar-nav-item { width: auto; flex: 0 0 auto; white-space: nowrap; margin-bottom: 0; padding: 8px 10px; }
          .sidebar-signout { margin-top: 0; }
          .page { padding: 20px 16px 28px; max-width: none !important; }
          .stats-grid-4, .stats-grid-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .form-grid-2, .form-grid-3, .two-col-grid { grid-template-columns: 1fr; }
          .toolbar-row { flex-direction: column; align-items: stretch; }
          .doctor-slot-row { grid-template-columns: 1fr; gap: 8px; }
          .admin-appointment-row { grid-template-columns: 1fr; }
          .mobile-stack-row { flex-direction: column; align-items: stretch; }
          .mobile-stack-row > :last-child { align-items: flex-start !important; }
          .app-card { padding: 16px !important; }
          .weekday-grid { overflow-x: auto; grid-template-columns: repeat(7, minmax(44px, 1fr)); }
        }
        @media (max-width: 520px) {
          .page { padding: 16px 12px 24px; }
          .stats-grid-4, .stats-grid-3 { grid-template-columns: 1fr; }
          .sidebar-user-text { min-width: 120px; }
          .sidebar-nav-item { font-size: 12px; padding: 8px 9px; }
          h1 { font-size: 22px !important; }
        }
      `}</style>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={token ? <Navigate to={defaultRouteForRole(user?.role)} replace /> : <LoginScreen onLogin={handleLogin} />} />
        <Route path="/register" element={token ? <Navigate to={defaultRouteForRole(user?.role)} replace /> : <RegisterScreen onLogin={handleLogin} />} />

        {/* Protected routes — wrapped in sidebar layout */}
        <Route path="/*" element={
          <ProtectedRoute token={token}>
            <div className="app-shell">
              <Sidebar user={user} onLogout={handleLogout} />
              <main className="app-main">
                <Routes>
                  <Route path="/patient/dashboard" element={<PatientDashboard token={token} user={user} />} />
                  <Route path="/doctors" element={<SearchDoctors token={token} user={user} />} />
                  <Route path="/book/doctor/:doctorId" element={<BookAppointment token={token} user={user} />} />
                  <Route path="/doctor/dashboard" element={<DoctorDashboard token={token} user={user} />} />
                  <Route path="/doctor/schedule" element={<DoctorDashboard token={token} user={user} />} />
                  <Route path="/doctor/queue" element={<DoctorQueue token={token} user={user} />} />
                  <Route path="/doctor/reports" element={<DoctorReports token={token} user={user} />} />
                  <Route path="/doctor/availability" element={<SetAvailability token={token} user={user} />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard token={token} user={user} />} />
                  <Route path="/admin/walkin" element={<WalkIn token={token} user={user} />} />
                  <Route path="/admin/notifications" element={<SendNotification token={token} />} />
                  <Route path="/profile" element={<ProfilePage token={token} user={user} onUserUpdate={handleUserUpdate} />} />
                  <Route path="/" element={<Navigate to={defaultRouteForRole(user?.role)} replace />} />
                </Routes>
              </main>
            </div>
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}
